/* eslint-disable @typescript-eslint/no-unsafe-enum-comparison */
import { createFeatureSelector, createSelector } from '@ngrx/store';
import { MarginCalculatorState } from '../state/margin-calculator.state';
import { MarginState } from '../state/margin-state';
import { Facility } from '../../models/facility.model';
import { Clinician } from '../../models/clinician.model';
import { ProfessionSpecialtyModel } from '../../models/specialty.model';
import { MarginCalculatorStateHelpers } from '../state/margin-calculator-state-helpers';
import { RelocationType } from '../../enum/margin-calculator-reimbursement-type.enum';
import {
    CERT_OTHER_ID,
    CERT_OTHER_NAME,
    EducationReimbursement,
    Reimbursement,
    ReimbursementListItem,
    StateLicensureReimbursement
} from '../../models/reimbursement.model';
import { Job } from '../../models/job.model';
import Big from 'big.js';
import { ReimbursementsState } from '../state/reimbursements.state';
import { MarginCalculatorInsuranceHelperService } from '../../services/margin-calculator-insurance-helper.service';
import { Insurance } from '../../models/insurance.model';
import {
    InsuranceBenefitTypes,
    InsuranceIneligibleReasonTypes
} from '../../services/margin-calculator-insurance.service';
import { FacilityHelperService } from '../../services/facility-helper.service';
import { AverageWeeksPerMonth, FormulaHelperService } from '../../services/formula-helper.service';
import { formatNumber } from '@angular/common';
import { MarginCalculatorPayBillingHelperService } from '../../services/margin-calculator-pay-billing-helper.service';
import {
    BaseBillingRate,
    BillingRate,
    ContractBenefit,
    HOUSEWARE_OTHER_ID,
    Houseware,
    OrientationModel,
    PremiumBillingRateRequest,
    SampleMarginJob
} from '../../models';
import { marginCalculatorFeatureKey } from './margin-calculator.feature';
import { HousingDefaultState, HousingState, HousingTypeState } from '../state/housing.state';
import { HousingStateHelpers, HousingTypes } from '../state/housing-state-helpers';
import { FORM_NAMES } from '../../services/margin-calculator-form.service';
import { MarginsDifferences } from '../../models/margins-differences.model';
import { PayBillingStateHelpers } from '../state/pay-billing-state-helper';
import { ApiActionStatus, PickleTitles, PickleTitlesDisplay, PickleTitleCaption, RateTypes } from '../../enum';
import { HospitalType } from 'src/app/shared/lib/facility-rate/models/hospital-types.enum';
import { StipendState } from '../state/stipend-state';
import { Profession } from 'src/app/shared/models/profession.model';
import { UserInterfaceState } from '../state/user-interface-state';
import { UserInterfaceStateSpecialtyControl } from '../state/user-interface-specialty-control.state';
import { ControlDefaults } from '../../enum/control-defaults.enum';
import { FeatureFlagState } from '../state/feature-flag.state';
import { PathfindersFlags } from 'src/app/shared/models/enums/team-feature-flags/pathfinders-feature-flags.enum';
import { UserInterfaceStateFacilityControl } from '../state/user-interface-facility-control.state';
import { UserInterfaceStateClinicianControl } from '../state/user-interface-clinician-control.state';
import { UserCredentials } from '../../services/margin-calculator-auth.service';
import { ApiCallResponseState } from '../state/api-call-response.state';
import { ConfirmationDialogSettings } from 'src/app/shared/models/confirmation-dialog-settings.model';
import { MinimumWageDetails } from '../../models/minimum-wage-details.model';
import { RtoLimit } from '../../models/rto-limit.model';
import { addDays } from '@progress/kendo-date-math';
import { selectLoadedMargin } from './loaded-margin.selectors';
import { MarginType } from '../../enum/margin-type.enum';

export const selectMarginCalculatorState = createFeatureSelector<MarginCalculatorState>(marginCalculatorFeatureKey);

export const selectIsBusy = createSelector(selectMarginCalculatorState, (state: MarginCalculatorState) => state.isBusy);

export const selectErrorMessage = createSelector(
    selectMarginCalculatorState,
    (state: MarginCalculatorState) => state.errorMessage
);

export const selectFeatureFlags = createSelector(
    selectMarginCalculatorState,
    (state: MarginCalculatorState) => state?.featureFlags ?? {}
);

export const selectFixPayPackageShiftInfoFlag = createSelector(
    selectFeatureFlags,
    (featureFlags: FeatureFlagState) =>
        (featureFlags && featureFlags[PathfindersFlags.FixPayPackageShiftsInfo]) ?? false
);

//#region API Call Responses
export const selectApiCallResponses = createSelector(selectMarginCalculatorState, (state) => state.apiCallResponses);

export const selectGetPremiumBillRateApiResponseState = createSelector(
    selectApiCallResponses,
    (apiResponses: ApiCallResponseState) => apiResponses?.getPremiumBillRateApiResponse
);

export const selectGetPremiumBillRateApiResponse = createSelector(
    selectGetPremiumBillRateApiResponseState,
    (state) => state?.premiumBillRate
);
//#endregion

/**********************************************************
 * User Interface
 **********************************************************/
export const selectUserInterface = createSelector(
    selectMarginCalculatorState,
    (state: MarginCalculatorState) => state?.userInterfaceState
);
export const selectUserInterfaceSpecialtyControl = createSelector(
    selectUserInterface,
    (uiState: UserInterfaceState) => uiState?.specialtyControl
);

export const selectUserInterfaceIsInitialSpecialtyChanged = createSelector(
    selectUserInterfaceSpecialtyControl,
    (specialtyControl: UserInterfaceStateSpecialtyControl) => specialtyControl?.isInitialSpecialtyChanged
);

export const selectUserInterfaceFacilityControl = createSelector(
    selectUserInterface,
    (uiState: UserInterfaceState) => uiState?.facilityControl
);

export const selectUserInterfaceIsInitialFacilityChanged = createSelector(
    selectUserInterfaceFacilityControl,
    (facilityControl: UserInterfaceStateFacilityControl) => facilityControl?.isInitialFacilityChanged
);

export const selectUserInterfaceClinicianControl = createSelector(
    selectUserInterface,
    (uiState: UserInterfaceState) => uiState?.clinicianControl
);

export const selectUserInterfaceIsInitialClinicianChanged = createSelector(
    selectUserInterfaceClinicianControl,
    (clinicianControl: UserInterfaceStateClinicianControl) => clinicianControl?.isInitialClinicianChanged
);

export const selectUserInterfaceAnyControlChanged = createSelector(
    selectUserInterfaceIsInitialClinicianChanged,
    selectUserInterfaceIsInitialFacilityChanged,
    selectUserInterfaceIsInitialSpecialtyChanged,
    (isInitialClinicianChanged, isInitialFacilityChanged, isInitialSpecialtyChanged) =>
        !!(isInitialClinicianChanged || isInitialFacilityChanged || isInitialSpecialtyChanged)
);

export const selectIsMarginCreatedFromJob = createSelector(
    selectUserInterface,
    (uiState: UserInterfaceState) => uiState?.isMarginCreatedFromJob ?? false
);

/**********************************************************
 * Margin Form
 **********************************************************/

//#region Margin Form
export const selectMarginFormReveal = createSelector(
    selectMarginCalculatorState,
    (state: MarginCalculatorState) => state.marginFormReveal
);

export const selectRevealVacationDetails = createSelector(
    selectMarginFormReveal,
    (reveal: FORM_NAMES) => reveal === FORM_NAMES.vacation_details
);

export const selectRevealFacilityBillingDetails = createSelector(
    selectMarginFormReveal,
    (reveal: FORM_NAMES) => reveal === FORM_NAMES.facility_billing_details
);

export const selectRevealReimbursementsDetails = createSelector(
    selectMarginFormReveal,
    (reveal: FORM_NAMES) => reveal === FORM_NAMES.reimbursement_details
);

export const selectRevealHousingDetails = createSelector(
    selectMarginFormReveal,
    (reveal: FORM_NAMES) => reveal === FORM_NAMES.housing_details
);

export const selectRevealInsuranceDetails = createSelector(
    selectMarginFormReveal,
    (reveal: FORM_NAMES) => reveal === FORM_NAMES.insurance_details
);

export const selectRevealAdditionalCostsDetails = createSelector(
    selectMarginFormReveal,
    (reveal: FORM_NAMES) => reveal === FORM_NAMES.addtl_costs_details
);

export const selectRevealMarginDifferencesDetails = createSelector(
    selectMarginFormReveal,
    (reveal: FORM_NAMES) => reveal === FORM_NAMES.margins_differences_details
);

export const selectRevealCalculationDetails = createSelector(
    selectMarginFormReveal,
    (reveal: FORM_NAMES) => reveal === FORM_NAMES.calculation_details
);

export const selectRevealPayPackageDetails = createSelector(
    selectMarginFormReveal,
    (reveal) => reveal === FORM_NAMES.pay_package
);

export const selectRevealStipendDetails = createSelector(
    selectMarginFormReveal,
    (reveal) => reveal === FORM_NAMES.stipend_details
);
//#endregion

/**********************************************************
 * User credentials
 **********************************************************/
export const selectUserCredentials = createSelector(
    selectMarginCalculatorState,
    (state: MarginCalculatorState) => state?.userCredentials
);

export const selectUserId = createSelector(
    selectUserCredentials,
    (userCredentials: UserCredentials) => userCredentials?.userId
);

export const selectIsNurseContract = createSelector(
    selectUserCredentials,
    (userCredentials: UserCredentials) => userCredentials?.isNurseContract ?? false
);

export const selectIsRecruiter = createSelector(
    selectUserCredentials,
    (userCredentials: UserCredentials) => userCredentials?.isRecruiter ?? false
);

export const selectIsNurseContracting = createSelector(
    selectIsNurseContract,
    (isNurseContract: boolean) => isNurseContract
);

export const selectIsRecruiting = createSelector(
    selectIsNurseContracting,
    selectIsRecruiter,
    (hasNurseContracting: boolean, isRecruiter: boolean) => (hasNurseContracting ? false : isRecruiter)
);

/**********************************************************/
export const selectCliniciansByName = createSelector(
    selectMarginCalculatorState,
    (state: MarginCalculatorState) => state?.cliniciansByName
);

export const selectCliniciansByEmail = createSelector(
    selectMarginCalculatorState,
    (state: MarginCalculatorState) => state?.cliniciansByEmail
);

export const selectFacilities = createSelector(
    selectMarginCalculatorState,
    (state: MarginCalculatorState) => state?.facilities
);

export const selectBillingRates = createSelector(
    selectMarginCalculatorState,
    (state: MarginCalculatorState) => state?.billingRates
);

export const selectFirstBillRate = createSelector(selectBillingRates, (billingRates: BillingRate[]) =>
    billingRates ? billingRates[0] : null
);

export const selectFirstBillRateIncludeOvertime = createSelector(
    selectFirstBillRate,
    (rate) => rate?.includeOverTime ?? false
);

export const selectFirstBillRateIncludeCallback = createSelector(
    selectFirstBillRate,
    (rate) => rate?.includeCallBack ?? false
);

export const selectSpecialties = createSelector(
    selectMarginCalculatorState,
    (state: MarginCalculatorState) => state?.specialties
);

export const selectReimbursements = createSelector(
    selectMarginCalculatorState,
    (state: MarginCalculatorState) => state?.reimbursements
);

export const selectEducationReimbursementsFullList = createSelector(
    selectReimbursements,
    (reimbursements: Reimbursement) => reimbursements?.educationReimbursements
);

export const selectStateLicensureReimbursementsFullList = createSelector(
    selectReimbursements,
    (reimbursements: Reimbursement) => reimbursements?.stateLicensureReimbursements
);

export const selectGsaRates = createSelector(
    selectMarginCalculatorState,
    (state: MarginCalculatorState) => state?.gsaRates
);

export const selectHousewares = createSelector(
    selectMarginCalculatorState,
    (state: MarginCalculatorState) => state?.housewares
);

export const selectChangeOfContractMarginId = createSelector(
    selectMarginCalculatorState,
    (state: MarginCalculatorState) => state?.changeOfContractMarginId
);

export const selectMarginsDifferences = createSelector(
    selectMarginCalculatorState,
    (state: MarginCalculatorState) => state?.marginsDifferences
);

/**********************************************************
 * Margin
 **********************************************************/
export const selectMargin = createSelector(
    selectMarginCalculatorState,
    (state: MarginCalculatorState) => state?.margin
);

export const selectCreatedBy = createSelector(selectMargin, (margin: MarginState) => margin?.createdBy);

export const selectDateCreated = createSelector(selectMargin, (margin: MarginState) => margin?.dateCreated);

export const selectDuplicatedFromMarginId = createSelector(
    selectMargin,
    (margin: MarginState) => margin?.duplicatedFromMarginId
);

export const selectMarginType = createSelector(selectMargin, (margin: MarginState) => margin?.marginType);

export const selectMarginExists = createSelector(selectMargin, (margin: MarginState) => !!margin?.id);

export const selectBaseBillingRate = createSelector(selectMargin, (margin: MarginState) => margin?.baseBillingRate);

export const selectBaseBillingRateAlt = createSelector(
    selectMargin,
    (margin: MarginState) => margin?.baseBillingRateAlt
);

export const selectMarginId = createSelector(selectMargin, (margin: MarginState) => margin?.id);

export const selectSavedAfterMedicalDentalEffectiveInsurance = createSelector(
    selectMargin,
    (margin: MarginState) => margin?.savedAfterMedicalDentalEffectiveInsurance ?? false
);

export const selectContractId = createSelector(selectMargin, (margin: MarginState) => margin?.contractId);

export const selectIsPipelined = createSelector(selectContractId, (contractId: number) => contractId > 0);

export const selectContractStageCode = createSelector(selectMargin, (margin: MarginState) => margin?.contractStageCode);

export const selectContractStageDescription = createSelector(
    selectMargin,
    (margin: MarginState) => margin?.contractStageDescription ?? ''
);

export const selectIsOfferMargin = createSelector(
    selectMargin,
    (margin: MarginState) => margin?.isOfferMargin ?? false
);

export const selectOfferId = createSelector(selectMargin, (margin: MarginState) => margin?.offerId ?? 0);

export const selectStartDate = createSelector(
    selectMargin,
    (margin: MarginState) => margin?.startDate ?? new Date().toLocaleDateString()
);

export const selectStartDateAsDate = createSelector(selectStartDate, (startDate: string) =>
    startDate ? new Date(startDate) : null
);

export const selectEndDate = createSelector(
    selectMargin,
    (margin: MarginState) => margin?.endDate ?? new Date().toLocaleDateString()
);

export const selectEndDateAsDate = createSelector(selectEndDate, (endDate: string) =>
    endDate ? new Date(endDate) : null
);

export const selectWeeks = createSelector(selectMargin, (margin: MarginState) => margin?.weeks ?? 0);

export const selectLastWeeksLength = createSelector(
    selectMargin,
    (margin: MarginState) => margin?.lastWeeksLength ?? 0
);

export const selectMarginPercent = createSelector(selectMargin, (margin: MarginState) => margin?.marginPct ?? 0);

export const selectPendingOfficialOffer = createSelector(
    selectMargin,
    (margin: MarginState) => margin?.pendingOfficialOffer ?? false
);

export const selectIsVacation = createSelector(selectMargin, (margin: MarginState) => margin?.isVacation ?? false);

export const selectIsExtension = createSelector(selectMargin, (margin: MarginState) => margin?.isExtension ?? false);

export const selectShiftType = createSelector(selectMargin, (margin: MarginState) => margin?.altShift ?? 0);

export const selectIsRotatingShift = createSelector(selectShiftType, (shiftType: number) => shiftType === 2);

export const selectIsAlternatingShift = createSelector(selectShiftType, (shiftType: number) => shiftType === 1);

export const selectIsStandardShift = createSelector(
    selectIsRotatingShift,
    selectIsAlternatingShift,
    (isRotating: boolean, isAlternating: boolean) => !isRotating && !isAlternating
);

export const selectPendingFacilityApproval = createSelector(
    selectMargin,
    (margin: MarginState) => margin?.isPendingFacilityApproval ?? false
);

export const selectShiftHours = createSelector(selectMargin, (margin: MarginState) => margin?.hours ?? 0);

export const selectAltShiftHours = createSelector(selectMargin, (margin: MarginState) => margin?.hoursAlt ?? 0);

export const selectShifts = createSelector(selectMargin, (margin: MarginState) => margin?.shifts ?? 0);

export const selectAltShifts = createSelector(selectMargin, (margin: MarginState) => margin?.shiftsAlt ?? 0);

export const selectShiftTime = createSelector(selectMargin, (margin: MarginState) => margin?.shiftTime ?? 0);

export const selectStringShiftTime = createSelector(selectShiftTime, (shiftTime: number) =>
    MarginCalculatorPayBillingHelperService.convertShiftTimeToString(shiftTime)
);

export const selectAltShiftTime = createSelector(selectMargin, (margin: MarginState) => margin?.shiftTimeAlt ?? 0);

export const selectVacationDates = createSelector(selectMargin, (margin: MarginState) => margin?.vacationDates);

export const selectVacationDatesAsDates = createSelector(selectVacationDates, (vacationDates: string) =>
    vacationDates && vacationDates !== '' ? vacationDates.split(';').map((value) => new Date(value)) : []
);

export const selectBookingWeeks = createSelector(selectMargin, (margin: MarginState) => margin?.bookingWeeks ?? 0);

export const selectAltBookingWeeks = createSelector(
    selectMargin,
    (margin: MarginState) => margin?.altBookingWeeks ?? 0
);

export const selectContractDays = createSelector(selectMargin, (margin: MarginState) => margin?.contractDays ?? 0);

export const selectContractLastDaysLength = createSelector(
    selectMargin,
    (margin: MarginState) => margin?.lastDaysLength ?? 0
);

export const selectStartTimeFrameHours = createSelector(selectMargin, (margin: MarginState) => margin?.shiftH ?? 0);

export const selectStartTimeFrameMinutes = createSelector(selectMargin, (margin: MarginState) => margin?.shiftM ?? 0);

export const selectEndTimeFrameHours = createSelector(selectMargin, (margin: MarginState) => margin?.shiftToH ?? 0);

export const selectEndTimeFrameMinutes = createSelector(selectMargin, (margin: MarginState) => margin?.shiftToM ?? 0);

export const selectAltStartTimeFrameHours = createSelector(
    selectMargin,
    (margin: MarginState) => margin?.shiftAltH ?? 0
);

export const selectAltStartTimeFrameMinutes = createSelector(
    selectMargin,
    (margin: MarginState) => margin?.shiftAltM ?? 0
);

export const selectAltEndTimeFrameHours = createSelector(
    selectMargin,
    (margin: MarginState) => margin?.shiftAltToH ?? 0
);

export const selectAltEndTimeFrameMinutes = createSelector(
    selectMargin,
    (margin: MarginState) => margin?.shiftAltToM ?? 0
);

export const selectInternalCosts = createSelector(selectMargin, (margin: MarginState) => margin?.internalCosts ?? 0);

export const selectAyaBooster = createSelector(selectMargin, (margin: MarginState) => margin?.ayaBooster ?? 0);

export const selectBillingOvertimeEnabled = createSelector(
    selectMargin,
    (margin: MarginState) => margin?.isBillOverTime ?? false
);

export const selectFirstOvertimeHours = createSelector(
    selectMargin,
    (margin: MarginState) => margin?.overtimeHours ?? 0
);

export const selectSecondOvertimeHours = createSelector(
    selectMargin,
    (margin: MarginState) => margin?.billDailyOtAfter ?? 0
);

export const selectFirstOvertimeFrequency = createSelector(
    selectMargin,
    (margin: MarginState) => margin?.billDorW?.trim() || ControlDefaults.EmptySelect
);

export const selectSecondOvertimeFrequency = createSelector(
    selectMargin,
    (margin: MarginState) => margin?.billDorWM?.trim() || ControlDefaults.EmptySelect
);

export const selectAndOrBillingRule = createSelector(
    selectMargin,
    selectFeatureFlags,
    (margin: MarginState, featureFlags) => {
        const pinkCellOtBillFix = featureFlags[PathfindersFlags.PinkCellOtBillFix];
        if (pinkCellOtBillFix) {
            if (!margin?.andOr) {
                return ControlDefaults.EmptySelect;
            }
            return margin.andOr.trim() || ControlDefaults.EmptySelect;
        }
        return margin?.andOr ? margin.andOr.trim() : ControlDefaults.EmptySelect;
    }
);

export const selectMarginOvertimeInfo = createSelector(
    selectAndOrBillingRule,
    selectFirstOvertimeHours,
    selectSecondOvertimeHours,
    selectFirstOvertimeFrequency,
    selectSecondOvertimeFrequency,
    (andOr, firstOvertimeHours, secondOvertimeHours, firstOvertimeFrequency, secondOvertimeFrequency) => ({
        andOr,
        firstOvertimeHours,
        secondOvertimeHours,
        firstOvertimeFrequency,
        secondOvertimeFrequency
    })
);

export const selectFacilityFirstOvertimeHours = createSelector(
    selectBillingRates,
    selectShiftHours,
    (billingRates, shiftHours) => {
        return PayBillingStateHelpers.getOvertimeHoursRule(1, shiftHours, billingRates) ?? 0;
    }
);

export const selectFacilitySecondOvertimeHours = createSelector(
    selectBillingRates,
    selectShiftHours,
    (billingRates, shiftHours) => {
        return PayBillingStateHelpers.getOvertimeHoursRule(2, shiftHours, billingRates) ?? 0;
    }
);

export const selectFacilityFirstOvertimeFrequency = createSelector(
    selectBillingRates,
    selectShiftHours,
    (billingRates, shiftHours) => {
        return (
            PayBillingStateHelpers.getOvertimeHoursFrequency(1, shiftHours, billingRates)?.trim() ||
            ControlDefaults.EmptySelect
        );
    }
);

export const selectFacilitySecondOvertimeFrequency = createSelector(
    selectBillingRates,
    selectShiftHours,
    (billingRates, shiftHours) => {
        return (
            PayBillingStateHelpers.getOvertimeHoursFrequency(2, shiftHours, billingRates)?.trim() ||
            ControlDefaults.EmptySelect
        );
    }
);

export const selectFacilityAndOrRule = createSelector(
    selectBillingRates,
    selectShiftHours,
    (billingRates, shiftHours) => {
        return (
            PayBillingStateHelpers.getOvertimeAndOrRule(shiftHours, billingRates)?.trim() || ControlDefaults.EmptySelect
        );
    }
);

export const selectFacilityOvertimeInfo = createSelector(
    selectFacilityFirstOvertimeHours,
    selectFacilitySecondOvertimeHours,
    selectFacilityFirstOvertimeFrequency,
    selectFacilitySecondOvertimeFrequency,
    selectFacilityAndOrRule,
    (firstOvertimeHours, secondOvertimeHours, firstOvertimeFrequency, secondOvertimeFrequency, andOr) => ({
        firstOvertimeHours,
        secondOvertimeHours,
        firstOvertimeFrequency,
        secondOvertimeFrequency,
        andOr
    })
);

export const selectFirstOrientationHours = createSelector(
    selectMargin,
    (margin: MarginState) => margin?.orientationFirstHours ?? 0
);

export const selectNextOrientationHours = createSelector(
    selectMargin,
    (margin: MarginState) => margin?.orientationNextValue ?? 0
);

export const selectOrientationAmountIndicator = createSelector(
    selectMargin,
    (margin: MarginState) => margin?.orientationAmountIndicator ?? ''
);

export const selectOrientationNextAmountIndicator = createSelector(
    selectMargin,
    (margin: MarginState) => margin?.orientationNextAmountIndicator ?? ''
);

export const selectFirstOrientationAmount = createSelector(
    selectMargin,
    (margin: MarginState) => margin?.orientationAmount ?? 0
);

export const selectNextOrientationAmount = createSelector(
    selectMargin,
    (margin: MarginState) => margin?.orientationNextAmount ?? 0
);

export const selectNextOrientationType = createSelector(
    selectMargin,
    (margin: MarginState) => margin?.orientationNextType
);

export const selectOrientationType = createSelector(selectMargin, (margin) => margin?.orientationType ?? '');

export const selectOrientationNextType = createSelector(selectMargin, (margin) => margin?.orientationNextType ?? '');

export const selectOrientationAllHoursBillable = createSelector(
    selectMarginCalculatorState,
    (state) => state?.orientationAllHoursBillable ?? false
);

export const selectRegularPayHours = createSelector(selectMargin, (margin: MarginState) => margin?.regPayHrs ?? 0);

export const selectOvertimePayHours = createSelector(selectMargin, (margin: MarginState) => margin?.otPayHrs ?? 0);

export const selectWeeklyOvertimePayHours = createSelector(
    selectOvertimePayHours,
    selectBookingWeeks,
    (overTimePayHours, weeks) =>
        weeks > 0 ? new Big(overTimePayHours).div(weeks).round(2, Big.roundHalfUp).toNumber() : 0
);

export const selectAltRegularPayHours = createSelector(
    selectMargin,
    (margin: MarginState) => margin?.altRegPayHrs ?? 0
);

export const selectWeeklyAltRegularPayHours = createSelector(
    selectAltRegularPayHours,
    selectAltBookingWeeks,
    (altRegularPayHours: number, weeks: number) =>
        weeks > 0 ? new Big(altRegularPayHours).div(weeks).round(2, Big.roundHalfUp).toNumber() : 0
);

export const selectAltOvertimePayHours = createSelector(
    selectMargin,
    (margin: MarginState) => margin?.altOtPayHrs ?? 0
);

export const selectWeeklyAltOvertimePayHours = createSelector(
    selectAltOvertimePayHours,
    selectAltBookingWeeks,
    (altOvertimePayHours: number, weeks: number) =>
        weeks > 0 ? new Big(altOvertimePayHours).div(weeks).round(2, Big.roundHalfUp).toNumber() : 0
);

export const selectBillingRegularHours = createSelector(
    selectMargin,
    (margin: MarginState) => margin?.billingRegHours ?? 0
);

export const selectBillingOvertimeHours = createSelector(
    selectMargin,
    (margin: MarginState) => margin?.billingOtHours ?? 0
);

export const selectBillingTotalHours = createSelector(
    selectBillingRegularHours,
    selectBillingOvertimeHours,
    (regularHours: number, overtimeHours: number) => new Big(regularHours).add(overtimeHours).toNumber()
);

export const selectWeeklyHours = createSelector(selectMargin, (margin: MarginState) => margin?.weeklyHours ?? 0);

export const selectHalfwayDate = createSelector(selectMargin, (margin: MarginState) => margin?.halfwayDate);

export const selectImportedFromMarginId = createSelector(
    selectMargin,
    (margin: MarginState) => margin?.importedFromMarginId
);

export const selectIsLinkedContractRequestInDraftingStatus = createSelector(
    selectMargin,
    (margin: MarginState) => margin?.isLinkedContractRequestInDraftingStatus ?? false
);

export const selectCommonShiftInfo = createSelector(
    selectIsStandardShift,
    selectStringShiftTime,
    selectShiftHours,
    (isStandard: boolean, shiftTime: string, shiftHours: number) => ({ isStandard, shiftTime, shiftHours })
);

export const selectSampleMarginJobs = createSelector(
    selectMargin,
    (margin: MarginState) => margin?.sampleMarginJobs ?? []
);

export const selectIsWFDSampleMargin = createSelector(selectSampleMarginJobs, (sampleMarginJobs: SampleMarginJob[]) =>
    sampleMarginJobs?.some(
        (job) =>
            job.hospitalType === HospitalType.WorkforceDisruptionClinical ||
            job.hospitalType === HospitalType.WorkforceDisruptionNonClinical
    )
);

export const selectStartDateDifference = createSelector(
    selectMarginsDifferences,
    (marginsDifferences: MarginsDifferences[]) =>
        marginsDifferences?.some((difference) => difference.propertyName.toUpperCase() === 'STARTDATETEXT')
);

export const selectEndDateDifference = createSelector(
    selectMarginsDifferences,
    (marginsDifferences: MarginsDifferences[]) =>
        marginsDifferences?.some((difference) => difference.propertyName.toUpperCase() === 'ENDDATETEXT')
);

export const selectDaysLengthDifference = createSelector(
    selectMarginsDifferences,
    (marginsDifferences: MarginsDifferences[]) =>
        marginsDifferences?.some((difference) => difference.propertyName.toUpperCase() === 'DAYSLENGTH')
);

export const selectVacationDifference = createSelector(
    selectMarginsDifferences,
    (marginsDifferences: MarginsDifferences[]) =>
        marginsDifferences?.some(
            (difference) =>
                difference.propertyName.toUpperCase() === 'RTOTEXT' ||
                difference.propertyName.toUpperCase() === 'ISRTOAPPROVED'
        )
);

export const selectShiftTypeDifference = createSelector(
    selectMarginsDifferences,
    (marginsDifferences: MarginsDifferences[]) =>
        marginsDifferences?.some((difference) => difference.propertyName.toUpperCase() === 'SHIFTTYPE')
);

export const selectNumShiftsDifference = createSelector(
    selectMarginsDifferences,
    (marginsDifferences: MarginsDifferences[]) =>
        marginsDifferences?.some((difference) => difference.propertyName.toUpperCase() === 'NUMBEROFSHIFTSTEXT')
);

export const selectShiftHoursDifference = createSelector(
    selectMarginsDifferences,
    (marginsDifferences: MarginsDifferences[]) =>
        marginsDifferences?.some((difference) => difference.propertyName.toUpperCase() === 'SHIFTHOURSTEXT')
);

export const selectShiftTimeDifference = createSelector(
    selectMarginsDifferences,
    (marginsDifferences: MarginsDifferences[]) =>
        marginsDifferences?.some((difference) => difference.propertyName.toUpperCase() === 'SHIFTTIME')
);

export const selectShiftTimeFrameDifference = createSelector(
    selectMarginsDifferences,
    (marginsDifferences: MarginsDifferences[]) =>
        marginsDifferences?.some((difference) => difference.propertyName.toUpperCase() === 'SHIFTTIMEFRAMETEXT')
);

/**********************************************************
 * Reimbursements
 **********************************************************/

export const selectReimbursementsAndBonuses = createSelector(
    selectMargin,
    (margin: MarginState) => margin?.reimbursements
);

export const selectRelocationType = createSelector(
    selectMargin,
    (margin: MarginState) => margin?.relocationType ?? RelocationType.evenSplit
);

export const selectRelocationFrom = createSelector(selectMargin, (margin: MarginState) => margin?.relocationFrom ?? 0);

export const selectRelocationTo = createSelector(selectMargin, (margin: MarginState) => margin?.relocationTo ?? 0);

export const selectRelocationTotal = createSelector(
    selectRelocationFrom,
    selectRelocationTo,
    (from: number, to: number) => new Big(from).add(to).toNumber()
);

/**
 * Selects the state licensures that are selected (checkbox checked) by the user
 */
export const selectStateLicensures = createSelector(
    selectReimbursementsAndBonuses,
    (reimbursements: ReimbursementsState) => reimbursements?.stateLicensures ?? []
);

/**
 * Selects the full list of selected and unselected state licensures as an array of
 * ReimbursementListItems.  This is a list that is suitable for binding to a mat-selection-list.
 */
export const selectStateLicensuresFullList = createSelector(
    selectStateLicensureReimbursementsFullList,
    selectStateLicensures,
    (allItems: StateLicensureReimbursement[], selectedItems: StateLicensureReimbursement[]) =>
        allItems?.map((item) => {
            const selectedItem = selectedItems?.find((s) => s.code === item.code);
            return {
                id: item.id,
                name: item.name,
                amount: item.initialCost,
                code: item.code,
                initialCost: selectedItem?.initialCost ?? item.updatedCost ?? item.initialCost,
                originalCost: item.initialCost,
                selected: !!selectedItem
            } satisfies ReimbursementListItem;
        })
);

/**
 * Selects the education reimbursements that are selected (checked) by the user
 */
export const selectEducationReimbursements = createSelector(
    selectReimbursementsAndBonuses,
    selectEducationReimbursementsFullList,
    (reimbursements: ReimbursementsState, allItems: EducationReimbursement[]) => {
        const educationReimbursements = reimbursements?.educationReimbursements;
        const educationReimbursementsWithIds = educationReimbursements?.map((selectedCert) => {
            const selectedExists = allItems?.find((cert) => cert.name === selectedCert.name);
            if (selectedExists) {
                selectedCert = { ...selectedCert, id: selectedExists.id };
                return selectedCert;
            }
            return selectedCert;
        });
        return educationReimbursementsWithIds ?? [];
    }
);
export const selectCertificationOtherText = createSelector(
    selectReimbursementsAndBonuses,
    (reimbursements: ReimbursementsState) => reimbursements?.certificationTextOther ?? 'Other'
);

export const selectCertificationOtherValue = createSelector(
    selectReimbursementsAndBonuses,
    (reimbursements: ReimbursementsState) => reimbursements?.certificationValueOther ?? 0
);

/**
 * Selects the full list of selected and unselected education reimbursements as an array of
 * ReimbursementListItems.  This is a list that is suitable for binding to a mat-selection-list.
 */
export const selectEducationFullList = createSelector(
    selectEducationReimbursementsFullList,
    selectEducationReimbursements,
    selectCertificationOtherText,
    selectCertificationOtherValue,
    (
        allItems: EducationReimbursement[],
        selectedItems: EducationReimbursement[],
        otherText: string,
        otherValue: number
    ) =>
        allItems?.map((item) => {
            let selectedItem = selectedItems?.find((s) => s.id === item.id);
            if (selectedItem?.id === CERT_OTHER_ID) {
                if (otherText) {
                    selectedItem = { ...selectedItem, name: otherText, amount: otherValue };
                } else {
                    selectedItem = { ...selectedItem, name: CERT_OTHER_NAME, amount: otherValue };
                }
            } else if (item?.id === CERT_OTHER_ID) {
                if (otherText) {
                    item = { ...item, name: otherText, amount: otherValue };
                } else {
                    item = { ...item, name: CERT_OTHER_NAME, amount: otherValue };
                }
            }

            return {
                id: item.id,
                name: selectedItem ? selectedItem.name : item.name,
                amount: selectedItem ? selectedItem.amount : item.amount,
                code: null,
                initialCost: selectedItem ? selectedItem.amount : item.amount,
                originalCost: selectedItem ? selectedItem.amount : item.amount,
                selected: !!selectedItem
            } satisfies ReimbursementListItem;
        })
);

export const selectMarginReimbursements = createSelector(
    selectStateLicensures,
    selectEducationReimbursements,
    (stateLicensures: StateLicensureReimbursement[], educationReimbursements: EducationReimbursement[]) => ({
        stateLicensures,
        educationReimbursements
    })
);

/**********************************************************
 * Reimbursements - One Time
 **********************************************************/

export const selectCarRentalOneTime = createSelector(
    selectReimbursementsAndBonuses,
    (reimbursements: ReimbursementsState) => reimbursements?.carRentalOneTime ?? 0
);

export const selectFingerprintingOneTime = createSelector(
    selectReimbursementsAndBonuses,
    (reimbursements: ReimbursementsState) => reimbursements?.fingerPrintingOneTime ?? 0
);

export const selectParkingOneTime = createSelector(
    selectReimbursementsAndBonuses,
    (reimbursements: ReimbursementsState) => reimbursements?.parkingOneTime ?? 0
);

export const selectScrubsOneTime = createSelector(
    selectReimbursementsAndBonuses,
    (reimbursements: ReimbursementsState) => reimbursements?.scrubsOneTime ?? 0
);

export const selectTaxableModulesOneTime = createSelector(
    selectReimbursementsAndBonuses,
    (reimbursements: ReimbursementsState) => reimbursements?.taxableModulesOneTime ?? 0
);

export const selectOneTimeCustomReimbursementTitle = createSelector(
    selectReimbursementsAndBonuses,
    (reimbursements: ReimbursementsState) => reimbursements?.oneTimeCustomReimbursementTitle ?? ''
);

export const selectOneTimeCustomReimbursementAmount = createSelector(
    selectReimbursementsAndBonuses,
    (reimbursements: ReimbursementsState) => reimbursements?.oneTimeCustomReimbursementAmount ?? 0
);

export const selectHasOneTimeCustomReimbursement = createSelector(
    selectReimbursementsAndBonuses,
    (reimbursements: ReimbursementsState) => reimbursements?.hasOneTimeCustomReimbursement ?? false
);

/**********************************************************
 * Reimbursements - Monthly
 **********************************************************/
export const selectCarRentalMonthly = createSelector(
    selectReimbursementsAndBonuses,
    (reimbursements: ReimbursementsState) => reimbursements?.rentalCar ?? 0
);

export const selectParkingMonthly = createSelector(
    selectReimbursementsAndBonuses,
    (reimbursements: ReimbursementsState) => reimbursements?.parkingMonthly ?? 0
);

export const selectMonthlyCustomReimbursementTitle = createSelector(
    selectReimbursementsAndBonuses,
    (reimbursements: ReimbursementsState) => reimbursements?.monthlyCustomReimbursementTitle ?? ''
);

export const selectMonthlyCustomReimbursementAmount = createSelector(
    selectReimbursementsAndBonuses,
    (reimbursements: ReimbursementsState) => reimbursements?.monthlyCustomReimbursementAmount ?? 0
);

export const selectHasMonthlyCustomReimbursement = createSelector(
    selectReimbursementsAndBonuses,
    (reimbursements: ReimbursementsState) => reimbursements?.hasMonthlyCustomReimbursement ?? false
);

/**********************************************************
 * Reimbursements - Bonuses
 **********************************************************/
export const selectFacilityPaidBonus = createSelector(
    selectReimbursementsAndBonuses,
    (reimbursements: ReimbursementsState) => reimbursements?.facilityPaidBonus ?? 0
);

export const selectAyaPaidBonus = createSelector(
    selectReimbursementsAndBonuses,
    (reimbursements: ReimbursementsState) => reimbursements?.ayaPaidBonus ?? 0
);

export const selectDiscretionaryBonus = createSelector(
    selectReimbursementsAndBonuses,
    (reimbursements: ReimbursementsState) => reimbursements?.discretionaryBonus ?? 0
);

export const selectReferralBonus = createSelector(
    selectReimbursementsAndBonuses,
    (reimbursements: ReimbursementsState) => reimbursements?.referralBonus ?? 0
);

export const selectIsReferralBonusPopulated = createSelector(
    selectReferralBonus,
    (referralBonus: number) => referralBonus > 0
);

export const selectPayTo = createSelector(
    selectReimbursementsAndBonuses,
    (reimbursements: ReimbursementsState) => reimbursements?.payToBonus
);

export const selectForReferring = createSelector(
    selectReimbursementsAndBonuses,
    (reimbursements: ReimbursementsState) => reimbursements?.forReferringBonus
);

export const selectOfferAyaPaidBonus = createSelector(
    selectMarginCalculatorState,
    (state: MarginCalculatorState) => state?.offerAyaPaidBonus ?? 0
);

/**********************************************************
 * Reimbursements - Pickles
 **********************************************************/

/**
 * Selects the selected pickle's title that is read from and written to the database.
 */
export const selectSelectedPickleTitle = createSelector(
    selectReimbursementsAndBonuses,
    (reimbursements: ReimbursementsState) => reimbursements?.selectedPickleTitle ?? ''
);

/**
 * Selects the selected pickle's display text that will appear in the UI.
 */
export const selectSelectedPickleDisplay = createSelector(selectSelectedPickleTitle, (title: string) => {
    switch (title) {
        case PickleTitles.OneTimeCarRental:
            return PickleTitlesDisplay.OneTimeCarRental;
        case PickleTitles.OneTimeFingerprinting:
            return PickleTitlesDisplay.OneTimeFingerprinting;
        case PickleTitles.OneTimeParking:
            return PickleTitlesDisplay.OneTimeParking;
        case PickleTitles.OneTimeScrubs:
            return PickleTitlesDisplay.OneTimeScrubs;
        case PickleTitles.OneTimeTaxable:
            return PickleTitlesDisplay.OneTimeTaxable;
        case PickleTitles.OneTimeOtherCost:
            return PickleTitlesDisplay.OneTimeOtherCost;
        case PickleTitles.MonthlyCarRental:
            return PickleTitlesDisplay.MonthlyCarRental;
        case PickleTitles.MonthlyParking:
            return PickleTitlesDisplay.MonthlyParking;
        case PickleTitles.MonthlyOtherCost:
            return PickleTitlesDisplay.MonthlyOtherCost;
        case PickleTitles.Discretionary:
            return PickleTitlesDisplay.Discretionary;
        case PickleTitles.Referral:
            return PickleTitlesDisplay.Referral;
        default:
            return '';
    }
});

export const selectSelectedPickleCaption = createSelector(selectSelectedPickleTitle, (title: string) => {
    switch (title) {
        case PickleTitles.MonthlyCarRental:
        case PickleTitles.OneTimeCarRental:
            return PickleTitleCaption.CarRental;
        case PickleTitles.OneTimeFingerprinting:
            return PickleTitleCaption.Fingerprinting;
        case PickleTitles.MonthlyParking:
        case PickleTitles.OneTimeParking:
            return PickleTitleCaption.Parking;
        case PickleTitles.OneTimeScrubs:
            return PickleTitleCaption.Scrubs;
        case PickleTitles.OneTimeTaxable:
            return PickleTitleCaption.Taxable;
        case PickleTitles.OneTimeOtherCost:
        case PickleTitles.MonthlyOtherCost:
            return PickleTitleCaption.OtherCost;
        case PickleTitles.Discretionary:
            return PickleTitleCaption.Discretionary;
        case PickleTitles.Referral:
            return PickleTitleCaption.Referral;
        default:
            return '';
    }
});

export const selectIsMonthlyAmountPickled = createSelector(
    selectSelectedPickleTitle,
    (title: string) =>
        title === PickleTitles.MonthlyCarRental ||
        title === PickleTitles.MonthlyParking ||
        title === PickleTitles.MonthlyOtherCost
);

export const selectSelectedPickleAmount = createSelector(
    selectReimbursementsAndBonuses,
    (reimbursements: ReimbursementsState) => reimbursements?.selectedPickleAmount ?? 0
);

export const selectPickledAmount = createSelector(
    selectReimbursementsAndBonuses,
    (reimbursements: ReimbursementsState) => reimbursements?.pickledAmount ?? 0
);

export const selectPickledAmountTotal = createSelector(
    selectPickledAmount,
    selectIsMonthlyAmountPickled,
    selectWeeks,
    (pickledAmount: number, isMonthly: boolean, weeks: number) =>
        isMonthly ? new Big(pickledAmount).times(weeks).div(AverageWeeksPerMonth).toNumber() : pickledAmount
);

export const selectIsTaxableModulePickled = createSelector(
    selectSelectedPickleTitle,
    (title: string) => title === PickleTitles.OneTimeTaxable
);

export const selectIsDiscretionaryPickled = createSelector(
    selectSelectedPickleTitle,
    (title: string) => title === PickleTitles.Discretionary
);

export const selectIsReferralPickled = createSelector(
    selectSelectedPickleTitle,
    (title: string) => title === PickleTitles.Referral
);

export const selectIsBonusPickled = createSelector(
    selectIsDiscretionaryPickled,
    selectIsReferralPickled,
    (discretionary: boolean, referral: boolean) => discretionary || referral
);
export const selectIsReimbursmentPickled = createSelector(
    selectSelectedPickleTitle,
    (title: string) =>
        title === PickleTitles.OneTimeCarRental ||
        title === PickleTitles.OneTimeFingerprinting ||
        title === PickleTitles.OneTimeParking ||
        title === PickleTitles.OneTimeScrubs ||
        title === PickleTitles.OneTimeTaxable ||
        title === PickleTitles.OneTimeOtherCost ||
        title === PickleTitles.MonthlyCarRental ||
        title === PickleTitles.MonthlyParking
);

/**********************************************************
 * Reimbursements - Totals
 **********************************************************/
export const selectLicenseTotal = createSelector(
    selectStateLicensures,
    (stateLicensures: StateLicensureReimbursement[]) =>
        stateLicensures?.reduce(
            (accumulator, currentValue) => new Big(accumulator).add(currentValue.initialCost).toNumber(),
            0
        ) ?? 0
);

export const selectCertTotal = createSelector(
    selectEducationReimbursements,
    selectCertificationOtherValue,
    (educationReimbursements: EducationReimbursement[], otherValue: number) =>
        educationReimbursements?.reduce((accumulator, currentValue) => {
            const amount = currentValue.id === CERT_OTHER_ID ? otherValue : currentValue.amount;
            return new Big(accumulator).add(amount).toNumber();
        }, 0) ?? 0
);

export const selectBonusTotal = createSelector(
    selectFacilityPaidBonus,
    selectDiscretionaryBonus,
    selectAyaPaidBonus,
    selectReferralBonus,
    (facilityPaidBonus: number, discretionaryBonus: number, ayaPaidBonus: number, referralBonus: number) =>
        new Big(facilityPaidBonus).add(discretionaryBonus).add(ayaPaidBonus).add(referralBonus).toNumber()
);

export const selectOneTimeTotal = createSelector(
    selectCarRentalOneTime,
    selectFingerprintingOneTime,
    selectParkingOneTime,
    selectScrubsOneTime,
    selectTaxableModulesOneTime,
    selectOneTimeCustomReimbursementAmount,
    (
        carRentalOneTime: number,
        fingerprintingOneTime: number,
        parkingOneTime: number,
        scrubsOneTime: number,
        taxableModulesOneTime: number,
        oneTimeCustomReimbursement: number
    ) =>
        new Big(carRentalOneTime)
            .add(fingerprintingOneTime)
            .add(parkingOneTime)
            .add(scrubsOneTime)
            .add(taxableModulesOneTime)
            .add(oneTimeCustomReimbursement)
            .toNumber()
);

export const selectMonthlyTotal = createSelector(
    selectCarRentalMonthly,
    selectParkingMonthly,
    selectMonthlyCustomReimbursementAmount,
    (carRentalMonthly: number, parkingMonthly: number, monthlyCustomReimbursement: number) =>
        new Big(carRentalMonthly).add(parkingMonthly).add(monthlyCustomReimbursement).toNumber()
);

export const selectContractTotal = createSelector(
    selectMonthlyTotal,
    selectWeeks,
    (monthlyTotal: number, weeks: number) => monthlyTotal * (12 / 52) * weeks
);

/**********************************************************
 * Facility
 **********************************************************/
export const selectFacility = createSelector(selectMargin, (margin: MarginState) => margin?.facility);

export const selectFacilityGroupOrientationType = createSelector(
    selectMarginCalculatorState,
    (state: MarginCalculatorState) => state?.facilityContractRules?.orientation?.nonBillableHours[0]?.rateType
);

export const selectFacilityGroupOrientationNextType = createSelector(
    selectMarginCalculatorState,
    (state: MarginCalculatorState) => state?.facilityContractRules?.orientation?.nonBillableHours[1]?.rateType
);

export const selectFacilityId = createSelector(selectFacility, (facility: Facility) => facility?.hospitalId);

export const selectFacilityName = createSelector(selectFacility, (facility: Facility) => facility?.hospitalName);

export const selectLocation = createSelector(selectFacility, (facility: Facility) =>
    facility ? `${facility.hospitalCity} ${facility.hospitalState?.substring(0, 2)} ${facility.hospitalZip}` : ''
);

export const selectFacilityState = createSelector(
    selectFacility,
    (facility: Facility) => facility?.hospitalState?.substring(0, 2) ?? ''
);

export const selectFacilityExtDiscount = createSelector(
    selectFacility,
    (facility: Facility) => facility?.extDiscount ?? 0
);

export const selectFacilityExtDiscountCalculated = createSelector(selectFacility, (facility: Facility) =>
    facility?.extDiscount ? new Big(facility.extDiscount).times(100).toNumber() : 0
);

export const selectFacilityExtDiscountType = createSelector(selectFacility, (facility: Facility) =>
    Boolean(facility?.extDiscountType)
);

export const selectVmsProvider = createSelector(selectFacility, (facility: Facility) => facility?.vmsProvider);

export const selectAccountManagerId = createSelector(
    selectFacility,
    (facility: Facility) => facility?.accountManagerId
);

export const selectAccountManagerName = createSelector(
    selectFacility,
    (facility: Facility) => facility?.accountManagerName ?? ''
);

export const selectAccountManagerEmail = createSelector(
    selectFacility,
    (facility: Facility) => facility?.accountManagerEmail ?? ''
);

export const selectAccountCoordinatorEmail = createSelector(
    selectFacility,
    (facility: Facility) => facility?.accountCoordinatorEmail ?? ''
);

export const selectFpAmountInternal = createSelector(
    selectFacility,
    selectIsExtension,
    (facility: Facility, isExtension) => (isExtension ? 0 : (facility?.fpAmountInternal ?? 0))
);

export const selectFacilityMaxLodging = createSelector(
    selectFacility,
    (facility: Facility) => facility?.maxLodging ?? 0
);

export const selectFacilityOrientation = createSelector(selectFacility, (facility: Facility) => facility?.orientation);

export const selectFacilityOrientationHours = createSelector(
    selectFacilityOrientation,
    selectIsExtension,
    (orientation: OrientationModel, isExtension) => (isExtension ? 0 : (orientation?.orientHours ?? 0))
);

export const selectFacilityOrientationHoursAlt = createSelector(
    selectFacilityOrientation,
    selectIsExtension,
    (orientation: OrientationModel, isExtension) => (isExtension ? 0 : (orientation?.orientHoursAlt ?? 0))
);

export const selectFacilityOrientationRate = createSelector(
    selectFacilityOrientation,
    selectIsExtension,
    (orientation: OrientationModel, isExtension) => (isExtension ? 0 : (orientation?.orientRate ?? 0))
);

export const selectFacilityOrientationRateAlt = createSelector(
    selectFacilityOrientation,
    selectIsExtension,
    (orientation: OrientationModel, isExtension) => (isExtension ? 0 : (orientation?.orientRateAlt ?? 0))
);

export const selectFacilityOrientationType = createSelector(
    selectFacilityOrientation,
    (orientation: OrientationModel) => orientation?.orientType ?? ''
);

export const selectFacilityOrientationTypeAlt = createSelector(
    selectFacilityOrientation,
    (orientation: OrientationModel) => orientation?.orientTypeAlt ?? ''
);

export const selectIsFacilityInvalid = createSelector(
    selectMarginCalculatorState,
    (state: MarginCalculatorState) => state.isFacilityInvalid ?? false
);

export const selectIsClinicianInvalid = createSelector(
    selectMarginCalculatorState,
    (state: MarginCalculatorState) => state.isClinicianInvalid ?? false
);

export const selectFirstOrientationType = createSelector(
    selectMargin,
    selectFacilityOrientationType,
    (margin: MarginState, orientationType) => {
        return margin?.id > 0 ? margin.orientationType : orientationType;
    }
);

export const selectSecondOrientationType = createSelector(
    selectMargin,
    selectFacilityOrientationTypeAlt,
    (margin: MarginState, orientationTypeAlt) => {
        return margin?.id > 0 ? margin.orientationNextType : orientationTypeAlt;
    }
);

export const selectMarginOrientationAllHoursBillable = createSelector(selectMargin, (margin: MarginState) => {
    return margin?.id > 0 ? margin.orientationAllHoursBillable : false;
});

/**********************************************************
 * Housing
 **********************************************************/
export const selectHousing = createSelector(selectMargin, (margin: MarginState) => margin?.housing);
export const selectIsAyaHousing = createSelector(
    selectHousing,
    (housing: HousingState) => housing?.isAyaHousing ?? false
);
export const selectHousingType = createSelector(selectHousing, (housing: HousingState) => housing?.housingType);
export const selectHousingTypeId = createSelector(
    selectHousingType,
    (housingType: HousingTypeState) => housingType?.id
);
export const selectHousingTypeName = createSelector(
    selectHousingType,
    (housingType: HousingTypeState) => housingType?.name
);
export const selectHousingTypeIsApt = createSelector(
    selectHousingTypeId,
    (housingTypeId) => housingTypeId === HousingTypes.Apt
);
export const selectHousingTypeIsHotel = createSelector(
    selectHousingTypeId,
    (housingTypeId) => housingTypeId === HousingTypes.Hotel
);
export const selectContinuedHousing = createSelector(
    selectHousing,
    (housing: HousingState) => housing?.continuedHousing ?? false
);
export const selectAptMonthlyRent = createSelector(
    selectIsAyaHousing,
    selectHousingTypeIsApt,
    selectHousing,
    selectFacilityMaxLodging,
    (isAyaHousing: boolean, housingTypeIsApt: boolean, housing: HousingState, maxLodging: number) =>
        isAyaHousing && housingTypeIsApt ? ((!housing?.monthlyHousing ? maxLodging : housing?.monthlyHousing) ?? 0) : 0
);
export const selectHotelMonthlyRent = createSelector(
    selectIsAyaHousing,
    selectHousingTypeIsHotel,
    selectHousing,
    (isAyaHousing: boolean, housingTypeIsHotel: boolean, housing: HousingState) =>
        isAyaHousing && housingTypeIsHotel ? (housing?.extendedStayHotel ?? 0) : 0
);
export const selectMonthlyRent = createSelector(
    selectAptMonthlyRent,
    selectHotelMonthlyRent,
    (aptMonthlyRent: number, hotelMonthlyRent: number) => new Big(aptMonthlyRent).add(hotelMonthlyRent).toNumber()
);
export const selectMoveInDate = createSelector(selectHousing, (housing: HousingState) => housing?.moveInDate);
export const selectMoveInDateAsDate = createSelector(selectMoveInDate, (moveInDate: string) =>
    moveInDate ? new Date(moveInDate) : null
);
export const selectMoveOutDate = createSelector(selectHousing, (housing: HousingState) => housing?.moveOutDate);
export const selectMoveOutDateAsDate = createSelector(selectMoveOutDate, (moveOutDate: string) =>
    moveOutDate ? new Date(moveOutDate) : null
);
export const selectHousingDays = createSelector(
    selectMoveInDateAsDate,
    selectMoveOutDateAsDate,
    (startDate: Date, endDate: Date) => HousingStateHelpers.getDays(startDate, endDate)
);
export const selectContractDaysForHousing = createSelector(
    selectStartDateAsDate,
    selectEndDateAsDate,
    (startDate: Date, endDate: Date) => HousingStateHelpers.getDays(startDate, endDate)
);
export const selectProrateMultiplier = createSelector(
    selectHousingDays,
    selectContractDaysForHousing,
    (housingDays: number, contractDays: number) => HousingStateHelpers.getProrateMultiplier(housingDays, contractDays)
);
export const selectBedrooms = createSelector(selectHousing, (housing: HousingState) =>
    !housing?.bedrooms || housing?.bedrooms < 1 ? 1 : housing?.bedrooms
);
export const selectMoveOutExpenses = createSelector(
    selectHousing,
    selectIsAyaHousing,
    selectHousingTypeIsApt,
    (housing: HousingState, isAyaHousing, isApt) => (isAyaHousing && isApt ? housing?.moveOutExpenses : 0)
);
export const selectFurnitureDelivery = createSelector(
    selectHousing,
    selectIsAyaHousing,
    selectHousingTypeIsApt,
    (housing: HousingState, isAyaHousing, isApt) => (isAyaHousing && isApt ? housing?.furnitureDelivery : 0)
);
export const selectFurniturePerMonth = createSelector(
    selectHousing,
    selectIsAyaHousing,
    selectHousingTypeIsApt,
    (housing: HousingState, isAyaHousing, isApt) => (isAyaHousing && isApt ? housing?.furniture : 0)
);
export const selectUtilitiesPerMonth = createSelector(
    selectHousing,
    selectIsAyaHousing,
    selectHousingTypeIsApt,
    (housing: HousingState, isAyaHousing, isApt) => (isAyaHousing && isApt ? housing?.utilities : 0)
);
export const selectMonthlyRentPerContract = createSelector(
    selectMonthlyRent,
    selectWeeks,
    selectProrateMultiplier,
    (monthlyRent: number, weeks: number, prorateMultiplier: number) =>
        HousingStateHelpers.getProratedPerContractCost(monthlyRent, weeks, prorateMultiplier)
);
export const selectFurniturePerContract = createSelector(
    selectFurniturePerMonth,
    selectWeeks,
    selectProrateMultiplier,
    (furniture: number, weeks: number, prorateMultiplier: number) =>
        HousingStateHelpers.getProratedPerContractCost(furniture, weeks, prorateMultiplier)
);
export const selectUtilitiesPerContract = createSelector(
    selectUtilitiesPerMonth,
    selectWeeks,
    selectProrateMultiplier,
    (utilities: number, weeks: number, prorateMultiplier: number) =>
        HousingStateHelpers.getProratedPerContractCost(utilities, weeks, prorateMultiplier)
);
export const selectSelectedHousewares = createSelector(selectHousing, (housing: HousingState) =>
    housing?.isAyaHousing ? (housing?.housewares ?? []) : []
);
export const selectCombinedHousewares = createSelector(
    selectHousewares,
    selectSelectedHousewares,
    (allHousewares: Houseware[], selectedHousewares: Houseware[]) =>
        allHousewares?.map((houseware) => {
            const selected = selectedHousewares?.find((s) => s.id === houseware.id);
            if (selected) {
                if (houseware.id === HOUSEWARE_OTHER_ID) {
                    return { ...selected, selected: true };
                }
                return { ...houseware, selected: true };
            }
            return houseware;
        }) ?? []
);
export const selectOtherHouseware = createSelector(selectSelectedHousewares, (housewares: Houseware[]) =>
    housewares?.find((houseware) => houseware.id === HOUSEWARE_OTHER_ID)
);
export const selectOtherHousewareTitle = createSelector(
    selectOtherHouseware,
    (houseware: Houseware) => houseware?.title ?? ''
);
export const selectOtherHousewareCost = createSelector(
    selectOtherHouseware,
    (houseware: Houseware) => houseware?.avgCost ?? 0
);
export const selectTotalHousewares = createSelector(
    selectCombinedHousewares,
    (housewares: Houseware[]) =>
        housewares
            ?.filter((houseware) => houseware.selected && !isNaN(houseware.avgCost))
            .reduce((accumulator, currentValue) => accumulator + currentValue.avgCost, 0) ?? 0
);
export const selectTotalHousewaresContractCost = createSelector(
    selectTotalHousewares,
    selectWeeks,
    selectProrateMultiplier,
    (totalHousewares: number, weeks: number, prorateMultiplier: number) =>
        HousingStateHelpers.getProratedPerContractCost(totalHousewares, weeks, prorateMultiplier)
);
export const selectIsFamilyIncluded = createSelector(
    selectHousing,
    (housing: HousingState) => housing?.isFamilyIncluded ?? false
);
export const selectFamilyMemberRelationship = createSelector(
    selectHousing,
    (housing: HousingState) => housing?.familyRelationship
);
export const selectFamilyName = createSelector(selectHousing, (housing: HousingState) => housing?.familyName);
export const selectFamilyAge = createSelector(selectHousing, (housing: HousingState) => housing?.familyAge ?? 0);
export const selectFamilyNotes = createSelector(selectHousing, (housing: HousingState) => housing?.familyNotes);
export const selectIsPetIncluded = createSelector(
    selectHousing,
    (housing: HousingState) => housing?.isPetIncluded ?? false
);
export const selectPetType = createSelector(selectHousing, (housing: HousingState) => housing?.petType);
export const selectPetWeight = createSelector(selectHousing, (housing: HousingState) => housing?.petWeight ?? 0);
export const selectPetAge = createSelector(selectHousing, (housing: HousingState) => housing?.petAge ?? 0);
export const selectPetBreed = createSelector(selectHousing, (housing: HousingState) => housing?.petBreed);
export const selectHousingDefaults = createSelector(selectHousing, (housing: HousingState) => housing?.defaults);
export const selectMonthlyRentDefault = createSelector(
    selectHousingDefaults,
    (defaults: HousingDefaultState) => defaults?.monthlyHousing
);
export const selectMonthlyRentValidation = createSelector(
    selectMonthlyRentDefault,
    selectHousingTypeIsApt,
    (monthlyRentDefault, isApartment) => ({
        monthlyRentDefault,
        isApartment
    })
);

/**********************************************************
 * Job
 **********************************************************/
export const selectJob = createSelector(selectMarginCalculatorState, (state: MarginCalculatorState) => state.job);

export const selectJobId = createSelector(selectMargin, (margin: MarginState) => margin.jobId);

export const selectJobIsAlternateShift = createSelector(selectJob, (job: Job) => job?.isAlternateShift ?? false);

export const selectJobAyaPaidBonus = createSelector(selectJob, (job: Job) => job?.ayaPaidBonus ?? 0);
/**********************************************************
 * Clinician
 **********************************************************/
//#region Clinician
export const selectClinician = createSelector(selectMargin, (margin: MarginState) => margin?.clinician);

export const selectClinicianId = createSelector(selectClinician, (clinician: Clinician) => clinician?.userId);

export const selectClinicianName = createSelector(selectClinician, (clinician: Clinician) => clinician?.fullName);

export const selectClinicianEmail = createSelector(selectClinician, (clinician: Clinician) => clinician?.email);

export const selectRecruiter = createSelector(selectClinician, (clinician: Clinician) => clinician?.accountManager);

export const selectRecruiterId = createSelector(selectClinician, (clinician: Clinician) => clinician?.accountManagerId);

export const selectProfession = createSelector(selectClinician, (clinician: Clinician) => clinician?.profession);

export const selectProfessionId = createSelector(selectProfession, (profession: Profession) => profession?.id);

export const selectSavedMarginMedical = createSelector(selectMargin, (margin: MarginState) => margin?.isMedical);

export const selectSavedMarginDental = createSelector(selectMargin, (margin: MarginState) => margin?.isDental);

export const selectMarginMedical = createSelector(selectMargin, (margin: MarginState) => margin?.isMedical);

export const selectMarginDental = createSelector(selectMargin, (margin: MarginState) => margin?.isDental);

export const selectProfessionName = createSelector(
    selectProfession,
    (profession: Profession) => profession?.abbreviation
);

export const selectClinicianLink = createSelector(
    selectIsNurseContracting,
    selectClinicianId,
    (isNurseContracting: boolean, clinicianId: number) =>
        isNurseContracting
            ? `/#/travel-contracts/candidate/${clinicianId}?subcontractor=0`
            : `/#/recruiting/candidates/${clinicianId}/new-profile/about`
);

export const selectOriginalClinicianId = createSelector(
    selectMargin,
    (margin: MarginState) => margin?.originalClinicianId ?? 0
);

export const selectOriginalProfessionId = createSelector(
    selectMargin,
    (margin: MarginState) => margin?.originalProfessionId ?? 0
);

export const selectOriginalSpecialtyId = createSelector(
    selectMargin,
    (margin: MarginState) => margin?.originalSpecialtyId ?? 0
);

export const selectPreviousProfessionId = createSelector(
    selectMargin,
    (margin: MarginState) => margin?.previousProfessionId ?? 0
);
//#endregion

/**********************************************************
 * Specialty
 **********************************************************/
//#region Specialty
export const selectSpecialty = createSelector(selectMargin, (margin: MarginState) => margin?.specialty);

export const selectSpecialtyId = createSelector(
    selectSpecialty,
    selectJob,
    (specialty: ProfessionSpecialtyModel, job: Job) =>
        job?.expertiseId && !specialty?.id ? job.expertiseId : (specialty?.id ?? 0)
);

export const selectSpecialtyName = createSelector(
    selectSpecialty,
    (specialty: ProfessionSpecialtyModel) => specialty?.name
);

//#endregion

/**********************************************************
 * Contract Min Days Status
 **********************************************************/
//#region Contract Min Days Status
export const selectContractMinDays = createSelector(
    selectMarginCalculatorState,
    (state: MarginCalculatorState) => state.contractMinDays
);

export const selectContractMinDaysStatus = createSelector(
    selectContractMinDays,
    (contractMinDays) => contractMinDays?.status
);

export const selectContractMinDaysStatusParameters = createSelector(
    selectClinicianId,
    selectFacilityId,
    selectBaseBillingRate,
    selectStartDate,
    selectEndDate,
    (clinicianId, facilityId, baseBillingRate, startDate, endDate) => ({
        clinicianId,
        facilityId,
        baseBillingRate,
        startDate,
        endDate
    })
);
//#endregion

export const selectTotalBookingWeeks = createSelector(
    selectBookingWeeks,
    selectAltBookingWeeks,
    (bookingWeeks: number, altBookingWeeks: number) => new Big(bookingWeeks).add(altBookingWeeks).toNumber()
);

export const selectLabWorkCosts = createSelector(selectTotalBookingWeeks, (totalWeeks: number) =>
    new Big(18).times(totalWeeks).round(2, Big.roundHalfUp).toNumber()
);

export const selectAdditionalCosts = createSelector(
    selectInternalCosts,
    selectFpAmountInternal,
    selectLabWorkCosts,
    selectAyaBooster,
    (internalCosts: number, fpAmountInternal: number, labWorkCosts: number, ayaBooster: number) =>
        new Big(internalCosts)
            .add(fpAmountInternal)
            .add(labWorkCosts)
            .minus(ayaBooster)
            .round(2, Big.roundHalfUp)
            .toNumber()
);

/**********************************************************
 * Insurance Specialized Selectors
 **********************************************************/
//#region Insurance
export const selectIsExtendedBenefits = createSelector(
    selectMargin,
    (margin: MarginState) => margin?.isExtendedBenefits
);

export const selectInsuranceInfo = createSelector(selectMargin, (margin: MarginState) => margin?.insuranceInfo);

export const selectInsuranceDentalDays = createSelector(
    selectInsuranceInfo,
    selectIsExtendedBenefits,
    (insurance: Insurance, isExtendedBenefits) =>
        (insurance?.dentalDays ?? 0) + (isExtendedBenefits ? (insurance?.daysInGap ?? 0) : 0)
);

export const selectInsuranceMedicalDays = createSelector(
    selectInsuranceInfo,
    selectIsExtendedBenefits,
    (insurance: Insurance, isExtendedBenefits) =>
        (insurance?.medicalDays ?? 0) + (isExtendedBenefits ? (insurance?.daysInGap ?? 0) : 0)
);

/**
 * selects the number of medical days if exists. Otherwise, returns undefined.
 */
export const selectSavedMarginMedicalDays = createSelector(
    selectMargin,
    (margin: MarginState) => margin?.noOfMedicalDays ?? 0
);

/**
 * selects the number of dental days if exists. Otherwise, returns undefined.
 */
export const selectSavedMarginDentalDays = createSelector(
    selectMargin,
    (margin: MarginState) => margin?.noOfDentalDays ?? 0
);

export const selectMarginMedicalEffectiveDate = createSelector(
    selectMargin,
    selectMarginMedical,
    (margin: MarginState, isMedical: boolean) =>
        margin?.medicalEffectiveDate ? margin.medicalEffectiveDate : isMedical ? margin.startDate : ''
);

export const selectMarginDentalEffectiveDate = createSelector(
    selectMargin,
    selectMarginDental,
    (margin: MarginState, isDental: boolean) =>
        margin?.dentalEffectiveDate ? margin.dentalEffectiveDate : isDental ? margin.startDate : ''
);

export const selectMarginDentalEffectiveDateAsDate = createSelector(
    selectMarginDentalEffectiveDate,
    selectStartDateAsDate,
    selectMarginDental,
    (dentalEffectiveDate: string, startDate: Date, isDental: boolean) =>
        dentalEffectiveDate && dentalEffectiveDate !== ''
            ? new Date(dentalEffectiveDate)
            : isDental
              ? new Date(startDate)
              : null
);

export const selectMarginMedicalEffectiveDateAsDate = createSelector(
    selectMarginMedicalEffectiveDate,
    selectStartDateAsDate,
    selectMarginMedical,
    (medicalEffectiveDate: string, startDate: Date, isMedical: boolean) =>
        medicalEffectiveDate && medicalEffectiveDate !== ''
            ? new Date(medicalEffectiveDate)
            : isMedical
              ? new Date(startDate)
              : null
);

export const selectDental = createSelector(selectInsuranceInfo, (insurance: Insurance) => {
    const previousContractDaysInGapLimit = 30;
    const includeInsurance =
        insurance?.insuranceBenefitTypes === InsuranceBenefitTypes.NewBenefits ||
        insurance?.insuranceBenefitTypes === InsuranceBenefitTypes.Extension;
    if (
        includeInsurance &&
        insurance.mostRecentContractHasBenefits &&
        insurance.daysInGap < previousContractDaysInGapLimit
    ) {
        return MarginCalculatorInsuranceHelperService.getPreviouslyEnrolledDental(insurance?.previousContract);
    } else {
        return includeInsurance;
    }
});

export const selectMedical = createSelector(selectInsuranceInfo, (insurance: Insurance) => {
    const previousContractDaysInGapLimit = 30;
    const includeInsurance =
        insurance?.insuranceBenefitTypes === InsuranceBenefitTypes.NewBenefits ||
        insurance?.insuranceBenefitTypes === InsuranceBenefitTypes.Extension;
    if (
        includeInsurance &&
        insurance.mostRecentContractHasBenefits &&
        insurance.daysInGap < previousContractDaysInGapLimit
    ) {
        return MarginCalculatorInsuranceHelperService.getPreviouslyEnrolledMedical(insurance?.previousContract);
    } else {
        return includeInsurance;
    }
});

export const selectInsuranceBenefitsType = createSelector(
    selectMargin,
    (margin: MarginState) => margin?.insuranceBenefitTypes
);

export const selectOverrideIneligibility = createSelector(
    selectMargin,
    (margin: MarginState) => margin?.overrideIneligibility ?? false
);
export const selectRequireOverride = createSelector(
    selectMargin,
    (margin: MarginState) => margin.requireOverride ?? false
);

export const selectNewVersionDailyDentalCost = createSelector(
    selectInsuranceInfo,
    (insurance: Insurance) => insurance?.newVersionDailyDentalCost
);

export const selectNewVersionDailyMedicalCost = createSelector(
    selectInsuranceInfo,
    (insurance: Insurance) => insurance?.newVersionDailyMedicalCost ?? 0
);

export const selectDentalCost = createSelector(
    selectInsuranceInfo,
    (insurance: Insurance) => insurance?.dailyDentalCost ?? 0
);

export const selectMedicalCost = createSelector(
    selectInsuranceInfo,
    (insurance: Insurance) => insurance?.dailyMedicalCost ?? 0
);

export const selectInsuranceProviderId = createSelector(
    selectInsuranceInfo,
    (insurance: Insurance) => insurance?.providerId
);

export const selectInsurancePreviousContract = createSelector(
    selectInsuranceInfo,
    (insurance: Insurance) => insurance?.previousContract
);

export const selectLastEnrollmentMedical = createSelector(
    selectInsurancePreviousContract,
    (previousContract: ContractBenefit) => previousContract?.medical ?? false
);

export const selectLastEnrollmentDental = createSelector(
    selectInsurancePreviousContract,
    (previousContract: ContractBenefit) => previousContract?.dental ?? false
);

export const selectLastEnrollmentDate = createSelector(
    selectInsurancePreviousContract,
    (previousContract: ContractBenefit) => previousContract?.lastEnrollmentDate ?? ''
);

export const selectLastEnrollmentDateAsDate = createSelector(selectLastEnrollmentDate, (lastEnrollmentDate: string) =>
    lastEnrollmentDate ? new Date(lastEnrollmentDate) : null
);

export const selectClinicianPreviouslyEnrolled = createSelector(
    selectLastEnrollmentDate,
    (lastEnrollmentDate: string) => lastEnrollmentDate.length > 0
);

export const selectMarginDatePipelined = createSelector(selectMargin, (margin: MarginState) => margin?.datePipelined);

export const selectMarginDatePipelinedAsDate = createSelector(selectMarginDatePipelined, (pipelineDate: string) =>
    pipelineDate ? new Date(pipelineDate) : null
);

export const selectNeedInsurance = createSelector(selectMargin, (margin: MarginState) => margin?.isBenefit ?? false);

export const selectDaysInGapBeforeNewEffectiveDate = createSelector(
    selectInsuranceInfo,
    (insurance: Insurance) => insurance?.daysInGapBeforeNewEffectiveDate ?? 0
);

export const selectDaysInGapOnOrAfterNewEffectiveDate = createSelector(
    selectInsuranceInfo,
    (insurance: Insurance) => insurance?.daysInGapOnOrAfterNewEffectiveDate ?? 0
);

export const selectInsuranceEffectiveDates = createSelector(
    selectMarginCalculatorState,
    (state: MarginCalculatorState) => state?.insuranceEffectiveDates
);

export const selectNewEffectiveDate = createSelector(
    selectInsuranceEffectiveDates,
    (insuranceEffectiveDates) => insuranceEffectiveDates?.newEffectiveDate
);

export const selectOldEffectiveDate = createSelector(
    selectInsuranceEffectiveDates,
    (insuranceEffectiveDates) => insuranceEffectiveDates?.oldEffectiveDate
);

export const selectReleaseNewEffectiveDate = createSelector(
    selectInsuranceEffectiveDates,
    (insuranceEffectiveDates) => insuranceEffectiveDates?.releaseNewEffectiveDate
);

export const selectNewEffectiveDateAsDate = createSelector(selectNewEffectiveDate, (newEffectiveDate: string) =>
    newEffectiveDate ? new Date(newEffectiveDate) : null
);
export const selectOldEffectiveDateAsDate = createSelector(selectOldEffectiveDate, (oldEffectiveDate: string) =>
    oldEffectiveDate ? new Date(oldEffectiveDate) : null
);
export const selectReleaseNewEffectiveDateAsDate = createSelector(
    selectReleaseNewEffectiveDate,
    (releaseNewEffectiveDate: string) => (releaseNewEffectiveDate ? new Date(releaseNewEffectiveDate) : null)
);
export const selectIsNewOrPreviousSavedMargin = createSelector(selectMargin, (margin: MarginState) => {
    return !margin || (margin && margin.contractId === 0 && margin.id > 0);
});

export const selectInsuranceIneligibleReasons = createSelector(
    selectInsuranceInfo,
    (insurance: Insurance) => insurance?.insuranceIneligibleReasons ?? []
);

export const selectDaysInGap = createSelector(selectInsuranceInfo, (insurance: Insurance) => insurance?.daysInGap ?? 0);

export const selectTotalInsuranceDays = createSelector(
    selectInsuranceInfo,
    (insurance: Insurance) => insurance?.totalDays ?? 0
);

export const selectDaysInContract = createSelector(
    selectInsuranceInfo,
    (insurance: Insurance) => insurance?.daysInContract ?? 0
);

export const selectShowInsuranceDetails = createSelector(
    selectNeedInsurance,
    selectRequireOverride,
    selectOverrideIneligibility,
    (needInsurance, requireOverride, overridden) => needInsurance && (!requireOverride || overridden)
);

export const selectInsuranceMinimumRequiredHours = createSelector(
    selectInsuranceInfo,
    (insurance: Insurance) => insurance?.minimumRequiredHours ?? 30
);

export const selectInsuranceState = createSelector(
    selectInsuranceInfo,
    selectFacilityState,
    (insurance: Insurance, facilityState) => insurance?.state ?? facilityState
);

export const selectInsuranceOverrideDialogSettings = createSelector(
    selectInsuranceIneligibleReasons,
    selectInsuranceMinimumRequiredHours,
    selectInsuranceState,
    (ineligibleReasons, minimumRequiredHours, insuranceState) => {
        // the first part of this check should only show if there are no other ineligibility reasons
        let dialogMessage: string;
        if (
            ineligibleReasons.length === 1 &&
            ineligibleReasons.includes(InsuranceIneligibleReasonTypes.LessThan30DayBreakInEmployment)
        ) {
            dialogMessage = 'Break in employment is less than 30 days.<b> Insurance can only be added with a ';
            dialogMessage += 'qualifying event or during open enrollment</b>. If this applies to the candidate, ';
            dialogMessage += 'confirm insurance selection.';
        } else {
            dialogMessage = 'Candidate is ineligible for Insurance for the following reason(s):<br/><br/><ul>';

            if (ineligibleReasons.includes(InsuranceIneligibleReasonTypes.WeeklyHoursLessThanMinimumRequired)) {
                dialogMessage += `<li>Contracted hours are less than ${minimumRequiredHours} hours per week.</li>`;
            }
            if (ineligibleReasons.includes(InsuranceIneligibleReasonTypes.InsuranceUnavailableForState)) {
                dialogMessage += `<li>Insurance is not available in ${insuranceState}.</li>`;
            }

            dialogMessage += '</ul><br/>If you want to override ineligibility, confirm override selection.';
        }

        return {
            message: dialogMessage,
            action: 'CONFIRM',
            title: 'Confirm to Override Insurance Eligibility',
            needCancel: true,
            cancelText: 'CANCEL',
            needExtraPadding: false
        } satisfies ConfirmationDialogSettings;
    }
);

export const selectTotalMedicalCostCalculated = createSelector(
    selectMargin,
    (margin: MarginState) => margin?.totalMedicalCostCalculated ?? 0
);

export const selectTotalDentalCostCalculated = createSelector(
    selectMargin,
    (margin: MarginState) => margin?.totalDentalCostCalculated ?? 0
);

export const selectOverrideInsuranceIneligibility = createSelector(
    selectMargin,
    (margin: MarginState) => margin.overrideInsuranceIneligibility ?? false
);

export const selectRequireInsuranceOverride = createSelector(
    selectMargin,
    (margin: MarginState) => margin.requireInsuranceOverride ?? false
);

export const selectInsuranceDentalDetailInfoParams = createSelector(
    selectNewEffectiveDateAsDate,
    selectOldEffectiveDateAsDate,
    selectStartDateAsDate,
    selectEndDateAsDate,
    selectDaysInGap,
    selectIsExtendedBenefits,
    selectTotalInsuranceDays,
    selectDaysInContract,
    selectDaysInGapBeforeNewEffectiveDate,
    selectDaysInGapOnOrAfterNewEffectiveDate,
    selectMarginDatePipelinedAsDate,
    selectReleaseNewEffectiveDateAsDate,
    selectSavedMarginDentalDays,
    selectDentalCost,
    selectNewVersionDailyDentalCost,
    selectDental,
    selectNeedInsurance,
    (
        newEffectiveDate,
        oldEffectiveDate,
        startDateAsDate,
        endDateAsDate,
        daysInGap,
        isExtendedBenefits,
        totalDays,
        daysInContract,
        daysInGapBeforeNewEffectiveDate,
        daysInGapOnOrAfterNewEffectiveDate,
        marginDatePipelinedAsDate,
        releaseNewEffectiveDate,
        dentalDays,
        dentalCost,
        newVersionDentalCost,
        dental,
        needInsurance
    ) => ({
        newEffectiveDate,
        oldEffectiveDate,
        startDateAsDate,
        endDateAsDate,
        daysInGap,
        isExtendedBenefits,
        totalDays,
        daysInContract,
        daysInGapBeforeNewEffectiveDate,
        daysInGapOnOrAfterNewEffectiveDate,
        marginDatePipelinedAsDate,
        releaseNewEffectiveDate,
        dentalDays,
        dentalCost,
        newVersionDentalCost,
        dental,
        needInsurance
    })
);

export const selectInsuranceMedicalDetailInfoParams = createSelector(
    selectNewEffectiveDateAsDate,
    selectOldEffectiveDateAsDate,
    selectStartDateAsDate,
    selectEndDateAsDate,
    selectSavedMarginMedicalDays,
    selectMedicalCost,
    selectDaysInGap,
    selectNewVersionDailyMedicalCost,
    selectIsExtendedBenefits,
    selectTotalInsuranceDays,
    selectDaysInContract,
    selectDaysInGapBeforeNewEffectiveDate,
    selectDaysInGapOnOrAfterNewEffectiveDate,
    selectMarginDatePipelinedAsDate,
    selectReleaseNewEffectiveDateAsDate,
    selectMedical,
    selectNeedInsurance,
    (
        newEffectiveDate,
        oldEffectiveDate,
        startDateAsDate,
        endDateAsDate,
        medicalDays,
        medicalCost,
        daysInGap,
        newVersionMedicalCost,
        isExtendedBenefits,
        totalDays,
        daysInContract,
        daysInGapBeforeNewEffectiveDate,
        daysInGapOnOrAfterNewEffectiveDate,
        marginDatePipelinedAsDate,
        releaseNewEffectiveDate,
        medical,
        needInsurance
    ) => ({
        newEffectiveDate,
        oldEffectiveDate,
        startDateAsDate,
        endDateAsDate,
        medicalDays,
        medicalCost,
        daysInGap,
        newVersionMedicalCost,
        isExtendedBenefits,
        totalDays,
        daysInContract,
        daysInGapBeforeNewEffectiveDate,
        daysInGapOnOrAfterNewEffectiveDate,
        marginDatePipelinedAsDate,
        releaseNewEffectiveDate,
        medical,
        needInsurance
    })
);

export const selectInsuranceSelectionRecentTrigger = createSelector(
    selectMargin,
    (margin: MarginState) => margin?.insuranceSelectionRecentTrigger
);

export const selectTotalMedicalTotalDentalParams = createSelector(
    selectTotalMedicalCostCalculated,
    selectTotalDentalCostCalculated,
    (totalMedical, totalDental) => ({
        totalMedical,
        totalDental
    })
);

export const selectInsuranceTotalCostFixEffectiveDate = createSelector(
    selectMarginCalculatorState,
    (state: MarginCalculatorState) => state?.insuranceTotalCostFixEffectiveDate
);

export const selectIsPipelinedBeforeTotalCostFixEffectiveDate = createSelector(
    selectIsPipelined,
    selectMarginDatePipelinedAsDate,
    selectInsuranceTotalCostFixEffectiveDate,
    (isPipelined, marginDatePipelinedAsDate, insuranceTotalCostFixEffectiveDate) => {
        return (
            (isPipelined &&
                marginDatePipelinedAsDate &&
                insuranceTotalCostFixEffectiveDate &&
                MarginCalculatorStateHelpers.isBeforeDate(
                    marginDatePipelinedAsDate,
                    insuranceTotalCostFixEffectiveDate
                )) ??
            false
        );
    }
);

export const selectTotalInsuranceCostCalculated = createSelector(
    selectTotalMedicalTotalDentalParams,
    selectFeatureFlags,
    selectIsPipelinedBeforeTotalCostFixEffectiveDate,
    (total, featureFlags, isPipelinedBeforeTotalCostFixEffectiveDate) => {
        const insuranceTotalCostFix = featureFlags[PathfindersFlags.InsuranceTotalCostFix];
        if (!insuranceTotalCostFix || (insuranceTotalCostFix && isPipelinedBeforeTotalCostFixEffectiveDate)) {
            return Math.ceil(total.totalMedical + total.totalDental);
        }

        return total.totalMedical + total.totalDental;
    }
);
//#endregion

/**********************************************************
 * Stipends
 **********************************************************/
//#region Stipends
export const selectStipends = createSelector(selectMargin, (margin: MarginState) => margin.stipends);

export const selectHousingStipend = createSelector(
    selectStipends,
    (stipends: StipendState) => stipends?.housingStipend ?? 0
);

export const selectMealStipend = createSelector(selectStipends, (stipends) => stipends?.mealStipend ?? 0);

export const selectStipendsPerContract = createSelector(
    selectStipends,
    (stipends) => stipends?.stipendsPerContract ?? 0
);

export const selectMaxStipends = createSelector(selectStipends, (stipends) => stipends?.maxStipends ?? 0);

export const selectMaxMealStipend = createSelector(selectStipends, (stipends) => stipends?.maxMeals ?? 0);

export const selectMaxHousingStipend = createSelector(selectStipends, (stipends) => stipends?.maxHousing ?? 0);

export const selectWeeklyStipends = createSelector(selectStipends, (stipends) => stipends?.weeklyStipends ?? 0);

export const selectStipendsTotal = createSelector(
    selectMealStipend,
    selectHousingStipend,
    (meals: number, housing: number) => {
        return new Big(meals).add(housing).toNumber();
    }
);

export const selectAdjustedMaxStipends = createSelector(
    selectMaxStipends,
    selectMaxMealStipend,
    selectIsAyaHousing,
    (maxStipends, maxMealStipend, isAyaHousing) => (isAyaHousing ? maxMealStipend : maxStipends)
);

export const selectStipendsAreGreaterThanMax = createSelector(
    selectAdjustedMaxStipends,
    selectStipendsTotal,
    (maxStipends: number, weeklyStipends: number) => weeklyStipends > maxStipends
);

export const selectStipendsDetailAreGreaterThanMax = createSelector(
    selectMaxMealStipend,
    selectMaxHousingStipend,
    selectMealStipend,
    selectHousingStipend,
    (maxMeals: number, maxHousing: number, meals: number, housing: number) => {
        return meals > maxMeals || housing > maxHousing;
    }
);

export const selectWeeklyStipendDifference = createSelector(
    selectMarginsDifferences,
    (marginsDifferences: MarginsDifferences[]) =>
        marginsDifferences?.some((difference) => difference.propertyName.toUpperCase() === 'WEEKLYSTIPEND')
);
//#endregion

export const selectMealStipendPerDay = createSelector(selectStipends, (stipends) => stipends?.mealStipendPerDay ?? 0);

export const selectHousingStipendPerDay = createSelector(
    selectStipends,
    (stipends: StipendState) => stipends?.housingStipendPerDay ?? 0
);

export const selectTotalStipendPerDay = createSelector(
    selectMealStipendPerDay,
    selectHousingStipendPerDay,
    selectIsAyaHousing,
    (mealStipendPerDay: number, housingStipendPerDay: number, isAyaHousing: boolean) =>
        isAyaHousing ? mealStipendPerDay : new Big(housingStipendPerDay).add(mealStipendPerDay).toNumber()
);

export const selectMealStipendPerShift = createSelector(
    selectStipends,
    (stipends: StipendState) => stipends?.mealStipendPerShift ?? 0
);

export const selectHousingStipendPerShift = createSelector(
    selectStipends,
    (stipends: StipendState) => stipends?.housingStipendPerShift ?? 0
);

export const selectTotalStipendPerShift = createSelector(
    selectMealStipend,
    selectHousingStipend,
    selectShifts,
    (meals: number, housing: number, shifts: number) => {
        const perDayTotal = PayBillingStateHelpers.getStipendsPerDay(meals + housing);
        const perShiftTotal = PayBillingStateHelpers.getStipendsPerShift(perDayTotal, shifts);
        return perShiftTotal;
    }
);

export const selectGsaLink = createSelector(selectStartDateAsDate, selectFacility, (startDate, facility) =>
    PayBillingStateHelpers.buildGSALink(startDate, facility)
);

/**********************************************************
 * Pay and Billing
 **********************************************************/
//#region Pay and Billing
export const selectMarginPayRate = createSelector(selectMargin, (margin: MarginState) => {
    return margin?.marginPayRate ?? 20;
});

export const selectBaseBillingRateValue = createSelector(
    selectBaseBillingRate,
    (baseBillingRate: BaseBillingRate) => baseBillingRate?.value ?? 0
);

export const selectBaseBillingRateAltValue = createSelector(
    selectBaseBillingRateAlt,
    (baseBillingRateAlt: BaseBillingRate) => baseBillingRateAlt?.value ?? 0
);

export const selectSetBaseBillingRateParams = createSelector(
    selectShiftHours,
    selectShiftTime,
    selectBillingRates,
    selectBaseBillingRateValue,
    selectJob,
    (shiftHours, shiftTime, billingRates, marginBillRate, job) => ({
        shiftHours,
        shiftTime,
        billingRates,
        marginBillRate,
        job
    })
);

export const selectSetAltBaseBillingRateParams = createSelector(
    selectAltShiftHours,
    selectAltShiftTime,
    selectBillingRates,
    selectBaseBillingRateAltValue,
    selectJob,
    (altShiftHours, altShiftTime, billingRates, billRateAlt, job) => ({
        altShiftHours,
        altShiftTime,
        billingRates,
        billRateAlt,
        job
    })
);

export const selectPremiumRate = createSelector(selectMargin, (margin: MarginState) => margin?.premiumRate ?? 0);

export const selectOverTimeRate = createSelector(selectMargin, (margin: MarginState) => margin?.overTimeRate ?? 0);

export const selectOverTimeRateAlt = createSelector(
    selectMargin,
    (margin: MarginState) => margin?.overTimeRateAlt ?? 0
);

export const selectTaxableStipend = createSelector(selectMargin, (margin: MarginState) => margin?.taxableStipend ?? 0);

export const selectBlendedPayRate = createSelector(
    selectMarginCalculatorState,
    (state: MarginCalculatorState) => state.blendedPayRate ?? 0
);

export const selectBookingHoursSet = createSelector(
    selectBookingWeeks,
    selectAltBookingWeeks,
    selectRegularPayHours,
    selectOvertimePayHours,
    (bookingWeeks, altBookingWeeks, regularPayHours, overtimePayHours) => ({
        bookingWeeks,
        altBookingWeeks,
        regularPayHours,
        overtimePayHours
    })
);

export const selectCalculateStipendPerContractParams = createSelector(
    selectMealStipend,
    selectHousingStipend,
    selectTotalBookingWeeks,
    (mealStipend, housingStipend, totalBookingWeeks) => ({
        mealStipend,
        housingStipend,
        totalBookingWeeks
    })
);

export const selectBlendedPayRateParams = createSelector(
    selectFacilityState,
    selectMarginPayRate,
    selectPremiumRate,
    selectShiftHours,
    selectBookingHoursSet,
    (facilityState, marginPayRate, premiumRate, shiftHours) => ({
        facilityState,
        marginPayRate,
        premiumRate,
        shiftHours
    })
);

export const selectContractFees = createSelector(selectMargin, (margin: MarginState) => margin?.vmsFees ?? 0);

export const selectOriginalBillingRate = createSelector(
    selectBaseBillingRate,
    (baseBillingRate: BaseBillingRate) => baseBillingRate?.originalBillingRate ?? 0
);

export const selectOriginalBillingRateAlt = createSelector(
    selectBaseBillingRateAlt,
    (baseBillingRateAlt: BaseBillingRate) => baseBillingRateAlt?.originalBillingRate ?? 0
);

export const selectMarginExtDiscount = createSelector(selectMargin, (margin: MarginState) => margin?.extDiscount ?? 0);

export const selectMarginExtDiscountType = createSelector(
    selectMargin,
    (margin: MarginState) => margin?.extDiscountType ?? false
);

export const selectMarginDiscountPercent = createSelector(
    selectMargin,
    (margin: MarginState) => margin?.discountPercent ?? 0
);

export const selectMarginDiscountPercentDecimal = createSelector(selectMarginDiscountPercent, (discountPercent) =>
    discountPercent ? new Big(discountPercent).div(100).toNumber() : 0
);

export const selectMarginDetailPremiumBillRate = createSelector(selectMargin, (margin: MarginState) =>
    margin?.premiumBillRate ? new Big(margin?.premiumBillRate).round(2, Big.roundHalfUp).toNumber() : 0
);

export const selectShowBlendedPayRate = createSelector(
    selectFacility,
    (facility: Facility) => facility && FacilityHelperService.isSpecialState(facility)
);

export const selectMinPremiumPayRate = createSelector(
    selectMarginCalculatorState,
    (state: MarginCalculatorState) => state.minPremiumPayRate ?? 0
);

export const selectShiftBonus = createSelector(
    selectMarginCalculatorState,
    (state: MarginCalculatorState) => state.shiftBonus ?? 0
);

export const selectPremiumBillRateInvalid = createSelector(
    selectGetPremiumBillRateApiResponse,
    selectMarginDetailPremiumBillRate,
    (apiPremiumBillRate, premiumBillRate) => {
        return (
            FormulaHelperService.roundNumber(premiumBillRate || 0) !==
            FormulaHelperService.roundNumber(apiPremiumBillRate || 0)
        );
    }
);

export const selectAltBaseBillRateMatchesOriginal = createSelector(
    selectOriginalBillingRateAlt,
    selectBaseBillingRateAltValue,
    (originalBillingRateAlt, baseBillingRateAlt) =>
        formatNumber(originalBillingRateAlt, 'en-US', '1.2') !== formatNumber(baseBillingRateAlt, 'en-US', '1.2')
);

export const selectPremiumBillRateTrigger = createSelector(
    selectFirstOvertimeHours,
    selectSecondOvertimeHours,
    selectFirstOvertimeFrequency,
    selectSecondOvertimeFrequency,
    selectAndOrBillingRule,
    () => ({
        triggered: true
    })
);

export const selectBillingRateFullSet = createSelector(
    selectShiftHours,
    selectMarginExists,
    selectBaseBillingRateAltValue,
    (shiftHours, marginExists, marginBillRateAlt) => ({
        shiftHours,
        marginExists,
        marginBillRateAlt
    })
);

export const selectBaseOTBillRateCalculationData = createSelector(
    selectShiftHours,
    selectOriginalBillingRate,
    selectBaseBillingRateValue,
    (shiftHours, originalBillRate, baseBillRate) => ({
        shiftHours,
        originalBillRate,
        baseBillRate
    })
);

export const selectMarginBillingRulesSet = createSelector(
    selectShiftHours,
    selectOriginalBillingRate,
    selectMarginExists,
    selectBaseBillingRateValue,
    selectBaseBillingRateAltValue,
    (shiftHours, originalBillingRate, marginExists, baseBillRate, baseBillRateAlt) => ({
        shiftHours,
        originalBillingRate,
        marginExists,
        baseBillRate,
        baseBillRateAlt
    })
);

export const selectPremiumBillRateRequestParams = createSelector(
    selectShifts,
    selectShiftHours,
    selectFirstOrientationHours,
    selectSecondOvertimeHours,
    selectFirstOvertimeFrequency,
    selectSecondOvertimeFrequency,
    selectFacilityState,
    selectAndOrBillingRule,
    selectBaseBillingRateValue,
    selectOverTimeRate,
    selectBillingOvertimeEnabled,
    (
        shifts,
        hours,
        firstHours,
        secondHours,
        firstFrequency,
        secondFrequency,
        facilityState,
        otAndOr,
        baseBillRate,
        overTimeRate,
        otBillingRulesEnabled
    ) => ({
        shifts,
        hours,
        firstHours,
        secondHours,
        firstFrequency,
        secondFrequency,
        facilityState,
        otAndOr,
        baseBillRate,
        overTimeRate,
        otBillingRulesEnabled
    })
);

export const selectSetLockDownLogicParams = createSelector(
    selectShiftType,
    selectShiftHours,
    selectAltShiftHours,
    selectShifts,
    selectAltShifts,
    selectBillingOvertimeEnabled,
    selectFirstOvertimeHours,
    selectSecondOvertimeHours,
    selectFirstOvertimeFrequency,
    selectSecondOvertimeFrequency,
    selectAndOrBillingRule,
    selectIsAlternatingShift,
    selectFirstOvertimeHours,
    selectBillingRates,
    (
        shiftType,
        shiftHours,
        altShiftHours,
        shifts,
        altShifts,
        otBillingRulesEnabled,
        otFirstHour,
        otSecondHour,
        otFirstFrequency,
        otSecondFrequency,
        otAndOr,
        isAlternatingShift,
        firstOvertimeHours,
        billingRates
    ) => ({
        shiftType,
        shiftHours,
        altShiftHours,
        shifts,
        altShifts,
        otBillingRulesEnabled,
        otFirstHour,
        otSecondHour,
        otFirstFrequency,
        otSecondFrequency,
        otAndOr,
        isAlternatingShift,
        firstOvertimeHours,
        billingRates
    })
);

export const selectNonStandardPayHoursParams = createSelector(
    selectFacilityState,
    selectShifts,
    selectShiftHours,
    selectAltShifts,
    selectAltShiftHours,
    selectWeeks,
    selectStartDateAsDate,
    selectEndDateAsDate,
    selectVacationDatesAsDates,
    selectBookingWeeks,
    selectAltBookingWeeks,
    (
        state,
        shifts,
        shiftHours,
        altShifts,
        altShiftHours,
        weeks,
        from,
        to,
        vacationDates,
        bookingWeeks,
        altBookingWeeks
    ) => ({
        state,
        shifts,
        shiftHours,
        altShifts,
        altShiftHours,
        weeks,
        from,
        to,
        vacationDates,
        bookingWeeks,
        altBookingWeeks
    })
);

/** Select minimum holiday pay rate */
export const selectMinHolidayPayRate = createSelector(selectMarginPayRate, (marginPayRate) =>
    FormulaHelperService.calculateMinPremiumPayRate(marginPayRate)
);

/** Select minimum CallBack pay rate */
export const selectMinCallbackPayRate = createSelector(selectMarginPayRate, (marginPayRate) =>
    FormulaHelperService.calculateMinPremiumPayRate(marginPayRate)
);
//#endregion

/**********************************************************
 * facility-billing-details
 **********************************************************/
//#region Facility Billing Details
export const selectExtensionFees = createSelector(
    selectMarginExtDiscount,
    selectMarginExists,
    selectFacilityExtDiscountCalculated,
    (marginExtDiscount, marginExists, facilityExtDiscount) => {
        if (marginExists) {
            return marginExtDiscount;
        }
        return facilityExtDiscount;
    }
);

export const selectFacilityDiscountPercent = createSelector(
    selectFacility,
    (facility: Facility) => facility?.discountPercent ?? 0
);

export const selectFacilityContractFees = createSelector(selectBillingRates, (billingRates) =>
    billingRates && billingRates.length > 0 ? (billingRates[0]?.payBackPercent ?? 0) : 0
);

export const selectContractFeesUpdatedParams = createSelector(
    selectContractFees,
    selectFacilityContractFees,
    (marginContractFees, facilityContractFees) => ({ marginContractFees, facilityContractFees })
);

export const selectAdjustedContractFees = createSelector(selectContractFees, (contractFees) =>
    new Big(contractFees).div(100).toNumber()
);

export const selectExtDiscount = createSelector(selectBillingRates, (billingRates) =>
    billingRates ? billingRates[0].extDiscount : 0
);

export const selectAdjustedRate = (billRate: number) =>
    createSelector(
        selectAdjustedContractFees,
        selectMarginDiscountPercentDecimal,
        selectExtDiscount,
        (adjustedContractFees, marginDiscountPercentDecimal, extDiscount) =>
            FormulaHelperService.calculateAdjustedRate(
                billRate,
                adjustedContractFees,
                extDiscount / 100,
                marginDiscountPercentDecimal
            )
    );

export const selectMarginValue = (billRate: number, payRate: number) =>
    createSelector(selectAdjustedRate(billRate), selectFacilityState, (adjustedRate, facilityState) =>
        FormulaHelperService.calculateFacilityBillingMargin(
            payRate,
            MarginCalculatorPayBillingHelperService.determineMultiplier(facilityState),
            adjustedRate
        )
    );

export const selectHolidayPayRate = createSelector(selectMargin, (margin) => margin?.holidayPayRate ?? 0);

export const selectHolidayBillRate = createSelector(selectMargin, (margin) => margin?.holidayBillRate ?? 0);

export const selectIsHolidayPayRateLessThanMinium = createSelector(
    selectMarginPayRate,
    selectHolidayPayRate,
    (basePayRate, holidayPayRate) => holidayPayRate < FormulaHelperService.calculateMinPremiumPayRate(basePayRate)
);

export const selectExpectedHolidayBillRate = createSelector(
    selectShiftHours,
    selectBaseBillingRateValue,
    selectFirstBillRate,
    (shiftHours, baseBillRate, billingRate) =>
        MarginCalculatorPayBillingHelperService.getExpectedBillRateByType(
            RateTypes.Holiday,
            shiftHours,
            baseBillRate,
            billingRate,
            false
        ) ?? 0
);

export const selectHolidayBillRateForValidation = createSelector(
    selectShiftHours,
    selectBaseBillingRateValue,
    selectFirstBillRate,
    (shiftHours, baseBillRate, billingRate) =>
        MarginCalculatorPayBillingHelperService.getExpectedBillRateByType(
            RateTypes.Holiday,
            shiftHours,
            baseBillRate,
            billingRate,
            true
        ) ?? -1
);

export const selectOnCallPayRate = createSelector(selectMargin, (margin) => margin?.onCallPayRate ?? 0);

export const selectOnCallBillRate = createSelector(selectMargin, (margin) => margin?.onCallBillRate ?? 0);

export const selectExpectedOnCallBillRate = createSelector(
    selectShiftHours,
    selectBaseBillingRateValue,
    selectFirstBillRate,
    (shiftHours, baseBillRate, billingRate) =>
        MarginCalculatorPayBillingHelperService.getExpectedBillRateByType(
            RateTypes.OnCall,
            shiftHours,
            baseBillRate,
            billingRate,
            false
        ) ?? 0
);

export const selectOnCallBillRateForValidation = createSelector(
    selectShiftHours,
    selectBaseBillingRateValue,
    selectFirstBillRate,
    (shiftHours, baseBillRate, billingRate) =>
        MarginCalculatorPayBillingHelperService.getExpectedBillRateByType(
            RateTypes.OnCall,
            shiftHours,
            baseBillRate,
            billingRate,
            true
        ) ?? -1
);

export const selectCallbackPayRate = createSelector(selectMargin, (margin) => {
    return margin?.callbackPayRate ?? 0;
});

export const selectCallbackBillRate = createSelector(selectMargin, (margin) => margin?.callbackBillRate ?? 0);

export const selectIsCallbackPayRateLessThanMinimum = createSelector(
    selectMarginPayRate,
    selectCallbackPayRate,
    (basePayRate, callbackPayRate) => callbackPayRate < FormulaHelperService.calculateMinPremiumPayRate(basePayRate)
);

export const selectExpectedCallbackBillRate = createSelector(
    selectShiftHours,
    selectBaseBillingRateValue,
    selectFirstBillRate,
    (shiftHours, baseBillRate, billingRate) =>
        MarginCalculatorPayBillingHelperService.getExpectedBillRateByType(
            RateTypes.Callback,
            shiftHours,
            baseBillRate,
            billingRate,
            false
        ) ?? 0
);

export const selectCallbackBillRateForValidation = createSelector(
    selectShiftHours,
    selectBaseBillingRateValue,
    selectFirstBillRate,
    (shiftHours, baseBillRate, billingRate) =>
        MarginCalculatorPayBillingHelperService.getExpectedBillRateByType(
            RateTypes.Callback,
            shiftHours,
            baseBillRate,
            billingRate,
            true
        ) ?? -1
);

export const selectChargePayRate = createSelector(selectMargin, (margin) => margin?.chargePayRate ?? 0);

export const selectChargeBillRate = createSelector(selectMargin, (margin) => margin?.chargeBillRate ?? 0);
export const selectExpectedChargeBillRate = createSelector(
    selectShiftHours,
    selectBaseBillingRateValue,
    selectFirstBillRate,
    (shiftHours, baseBillRate, billingRate) =>
        MarginCalculatorPayBillingHelperService.getExpectedBillRateByType(
            RateTypes.Charge,
            shiftHours,
            baseBillRate,
            billingRate,
            false
        ) ?? 0
);

export const selectChargeBillRateForValidation = createSelector(
    selectShiftHours,
    selectBaseBillingRateValue,
    selectFirstBillRate,
    (shiftHours, baseBillRate, billingRate) =>
        MarginCalculatorPayBillingHelperService.getExpectedBillRateByType(
            RateTypes.Charge,
            shiftHours,
            baseBillRate,
            billingRate,
            true
        ) ?? -1
);

export const selectExpectedChargePayRate = createSelector(selectExpectedChargeBillRate, (chargeBillRate) =>
    chargeBillRate > 0 ? new Big(chargeBillRate).div(2).round(2, Big.roundHalfUp).toNumber() : 0
);

/**
 * Calculated charge pay rate value to be displayed.
 * Uses current payRate, billRate or marginPayRate - which value is not 0.
 * */
export const selectChargePayRateToDisplay = createSelector(
    selectChargePayRate,
    selectChargeBillRate,
    selectMarginPayRate,
    (chargePayRate, chargeBillRate, marginPayRate) =>
        chargePayRate > 0 ? chargePayRate + marginPayRate : chargeBillRate > 0 ? chargeBillRate / 2 + marginPayRate : 0
);

/**
 * Calculated charge bill rate value to be displayed.
 * Displays sum of form value and baseBillingRate or 0 if form value is empty.
 */
export const selectChargeBillRateToDisplay = createSelector(
    selectChargeBillRate,
    selectBaseBillingRateValue,
    (chargeBillRate, baseBillingRate) => (chargeBillRate > 0 ? chargeBillRate + baseBillingRate : 0)
);

export const selectMarginPayAndBillRates = createSelector(
    selectHolidayPayRate,
    selectHolidayBillRate,
    selectOnCallPayRate,
    selectOnCallBillRate,
    selectCallbackPayRate,
    selectCallbackBillRate,
    selectChargePayRate,
    selectChargeBillRate,
    (
        holidayPayRate,
        holidayBillRate,
        onCallPayRate,
        onCallBillRate,
        callbackPayRate,
        callbackBillRate,
        chargePayRate,
        chargeBillRate
    ) => ({
        holidayPayRate,
        holidayBillRate,
        onCallPayRate,
        onCallBillRate,
        callbackPayRate,
        callbackBillRate,
        chargePayRate,
        chargeBillRate
    })
);

export const selectDiscountPercentUpdatedParams = createSelector(
    selectMarginDiscountPercent,
    selectFacilityDiscountPercent,
    (marginDiscountPercent, facilityDiscountPercent) => ({ marginDiscountPercent, facilityDiscountPercent })
);

export const selectBillingPayMarginAddParameters = createSelector(
    selectFacilityState,
    selectAdjustedContractFees,
    selectMarginDiscountPercentDecimal,
    selectExtDiscount,
    (facilityState, adjustedContractFees, marginDiscountPercentDecimal, extDiscount) => ({
        facilityState,
        adjustedContractFees,
        marginDiscountPercentDecimal,
        extDiscount
    })
);

export const selectHolidayMargin = createSelector(
    selectBillingPayMarginAddParameters,
    selectHolidayBillRate,
    selectHolidayPayRate,
    selectMinHolidayPayRate,
    (
        { facilityState, adjustedContractFees, marginDiscountPercentDecimal, extDiscount },
        otBillRate,
        otPayRate,
        minHolidayPayRate
    ) => {
        const adjustedRate = FormulaHelperService.calculateAdjustedRate(
            otBillRate,
            adjustedContractFees,
            extDiscount / 100,
            marginDiscountPercentDecimal
        );

        const holidayPayRate = otPayRate || minHolidayPayRate;
        const margin = FormulaHelperService.calculateFacilityBillingMargin(
            holidayPayRate,
            MarginCalculatorPayBillingHelperService.determineMultiplier(facilityState),
            adjustedRate
        );

        return margin;
    }
);

export const selectChargeMargin = createSelector(
    selectBillingPayMarginAddParameters,
    selectChargeBillRate,
    selectChargePayRate,
    ({ facilityState, adjustedContractFees, marginDiscountPercentDecimal, extDiscount }, otBillRate, otPayRate) => {
        const adjustedRate = FormulaHelperService.calculateAdjustedRate(
            otBillRate,
            adjustedContractFees,
            extDiscount / 100,
            marginDiscountPercentDecimal
        );

        const margin = FormulaHelperService.calculateFacilityBillingMargin(
            otPayRate,
            MarginCalculatorPayBillingHelperService.determineMultiplier(facilityState),
            adjustedRate
        );

        return margin;
    }
);

export const selectOnCallMargin = createSelector(
    selectBillingPayMarginAddParameters,
    selectOnCallBillRate,
    selectOnCallPayRate,
    ({ facilityState, adjustedContractFees, marginDiscountPercentDecimal, extDiscount }, otBillRate, otPayRate) => {
        const adjustedRate = FormulaHelperService.calculateAdjustedRate(
            otBillRate,
            adjustedContractFees,
            extDiscount / 100,
            marginDiscountPercentDecimal
        );

        const margin = FormulaHelperService.calculateFacilityBillingMargin(
            otPayRate,
            MarginCalculatorPayBillingHelperService.determineMultiplier(facilityState),
            adjustedRate
        );

        return margin;
    }
);

export const selectCallBackMargin = createSelector(
    selectBillingPayMarginAddParameters,
    selectCallbackBillRate,
    selectCallbackPayRate,
    selectMinCallbackPayRate,
    (
        { facilityState, adjustedContractFees, marginDiscountPercentDecimal, extDiscount },
        otBillRate,
        otPayRate,
        minCallbackPayRate
    ) => {
        const adjustedRate = FormulaHelperService.calculateAdjustedRate(
            otBillRate,
            adjustedContractFees,
            extDiscount / 100,
            marginDiscountPercentDecimal
        );

        const callbackPayRate = otPayRate || minCallbackPayRate;

        const margin = FormulaHelperService.calculateFacilityBillingMargin(
            callbackPayRate,
            MarginCalculatorPayBillingHelperService.determineMultiplier(facilityState),
            adjustedRate
        );
        return margin;
    }
);
//#endregion

//#region Vacations
/**********************************************************
 * Calculations - Nurse Compensation
 **********************************************************/
export const selectPayRegularHours = createSelector(
    selectRegularPayHours,
    selectAltRegularPayHours,
    (primaryHours, alternateHours) => new Big(primaryHours).add(alternateHours).toNumber()
);
export const selectPayRegularHourly = createSelector(selectMarginPayRate, (basePayRate) => basePayRate ?? 0);
export const selectPayRegularGross = createSelector(selectPayRegularHourly, selectPayRegularHours, (rate, hours) =>
    new Big(rate).times(hours).toNumber()
);
export const selectPayOvertimeHours = createSelector(
    selectOvertimePayHours,
    selectAltOvertimePayHours,
    (overtimePayHours, altOvertimePayHours) => new Big(overtimePayHours).add(altOvertimePayHours).toNumber()
);
export const selectPayTotalHours = createSelector(selectPayRegularHours, selectPayOvertimeHours, (regular, overtime) =>
    new Big(regular).add(overtime).toNumber()
);
export const selectAdjustedPremiumRate = createSelector(
    selectFacilityState,
    selectMarginPayRate,
    selectPremiumRate,
    (state, basePayRate, premiumRate) =>
        PayBillingStateHelpers.calculateAdjustedPremiumRate(state, basePayRate, premiumRate)
);
export const selectPayOvertimeHourly = createSelector(selectAdjustedPremiumRate, (otPayRate) => otPayRate ?? 0);
export const selectPayOvertimeGross = createSelector(selectPayOvertimeHourly, selectPayOvertimeHours, (rate, hours) =>
    new Big(rate).times(hours).toNumber()
);
export const selectPayGrossTotal = createSelector(
    selectPayRegularGross,
    selectPayOvertimeGross,
    (payRegularGross, payOvertimeGross) => new Big(payRegularGross).add(payOvertimeGross).toNumber()
);
export const selectEstimatedPayGrossTotal = createSelector(
    selectPayGrossTotal,
    selectStipendsPerContract,
    (payGrossTotal, stipends) => new Big(payGrossTotal).add(stipends).toNumber()
);
export const selectPayHourlyTotal = createSelector(
    selectPayTotalHours,
    selectPayGrossTotal,
    (payTotalHours, payGrossTotal) =>
        payTotalHours ? new Big(payGrossTotal).div(payTotalHours).round(2, Big.roundHalfUp).toNumber() : 0
);
export const selectHourlyGrossPayTotal = createSelector(
    selectPayTotalHours,
    selectEstimatedPayGrossTotal,
    (payTotalHours, estimatedPayGrossTotal) =>
        payTotalHours ? new Big(estimatedPayGrossTotal).div(payTotalHours).round(2, Big.roundHalfUp).toNumber() : 0
);
export const selectHousingStipendsGrossTotal = createSelector(
    selectHousingStipend,
    selectTotalBookingWeeks,
    (housingStipend, bookingWeeks) => new Big(housingStipend).times(bookingWeeks).round(2, Big.roundHalfUp).toNumber()
);
export const selectHousingStipendsHourlyTotal = createSelector(
    selectHousingStipendsGrossTotal,
    selectPayTotalHours,
    (housingStipendGrossTotal, payHours) =>
        payHours ? new Big(housingStipendGrossTotal).div(payHours).round(2, Big.roundHalfUp).toNumber() : 0
);
export const selectMealStipendsGrossTotal = createSelector(
    selectMealStipend,
    selectTotalBookingWeeks,
    (mealStipend, bookingWeeks) => new Big(mealStipend).times(bookingWeeks).round(2, Big.roundHalfUp).toNumber()
);
export const selectMealStipendsHourlyTotal = createSelector(
    selectMealStipendsGrossTotal,
    selectPayTotalHours,
    (mealStipendGrossTotal, payHours) =>
        payHours ? new Big(mealStipendGrossTotal).div(payHours).round(2, Big.roundHalfUp).toNumber() : 0
);
export const selectRelocationGrossTotal = createSelector(
    selectRelocationTotal,
    (relocationGrossTotal) => relocationGrossTotal
);
export const selectRelocationHourlyTotal = createSelector(
    selectRelocationGrossTotal,
    selectPayTotalHours,
    (relocationGrossTotal, totalHours) =>
        totalHours ? new Big(relocationGrossTotal).div(totalHours).round(2, Big.roundHalfUp).toNumber() : 0
);
export const selectStateLicensuresTotals = createSelector(
    selectStateLicensures,
    selectPayTotalHours,
    (stateLicensures, totalHours) =>
        stateLicensures.map((stateLicensure) => {
            return {
                ...stateLicensure,
                hourlyTotal:
                    totalHours === 0
                        ? 0
                        : new Big(stateLicensure?.initialCost).div(totalHours).round(2, Big.roundHalfUp).toNumber()
            };
        })
);
export const selectStateLicensuresGrossTotal = createSelector(selectStateLicensuresTotals, (stateLicensures) =>
    stateLicensures.reduce((total, stateLicensure) => new Big(total).add(stateLicensure.initialCost).toNumber(), 0)
);
export const selectStateLicensuresHourlyTotal = createSelector(
    selectStateLicensuresGrossTotal,
    selectPayTotalHours,
    (stateLicensuresGrossTotal, totalHours) =>
        totalHours === 0 ? 0 : new Big(stateLicensuresGrossTotal).div(totalHours).round(2, Big.roundHalfUp).toNumber()
);
export const selectEducationReimbursementsTotals = createSelector(
    selectEducationReimbursements,
    selectPayTotalHours,
    (educationReimbursements, totalHours) =>
        educationReimbursements.map((educationReimbursement) => {
            return {
                ...educationReimbursement,
                hourlyTotal:
                    totalHours === 0
                        ? 0
                        : new Big(educationReimbursement?.amount).div(totalHours).round(2, Big.roundHalfUp).toNumber()
            };
        })
);
export const selectEducationReimbursementsGrossTotal = createSelector(
    selectEducationReimbursementsTotals,
    (educationReimbursements) =>
        educationReimbursements.reduce((total, educationReimbursement) => total + educationReimbursement.amount, 0)
);
export const selectEducationReimbursementsHourlyTotal = createSelector(
    selectEducationReimbursementsGrossTotal,
    selectPayTotalHours,
    (educationReimbursementsGrossTotal, totalHours) =>
        totalHours === 0
            ? 0
            : new Big(educationReimbursementsGrossTotal).div(totalHours).round(2, Big.roundHalfUp).toNumber()
);
export const selectOneTimeCarRentalGrossTotal = createSelector(
    selectCarRentalOneTime,
    (oneTimeCarRentalTotal) => oneTimeCarRentalTotal
);
export const selectOneTimeCarRentalHourlyTotal = createSelector(
    selectOneTimeCarRentalGrossTotal,
    selectPayTotalHours,
    (oneTimeCarRentalTotal, totalHours) =>
        totalHours === 0 ? 0 : new Big(oneTimeCarRentalTotal).div(totalHours).round(2, Big.roundHalfUp).toNumber()
);
export const selectMonthlyCarRentalGrossTotal = createSelector(
    selectCarRentalMonthly,
    selectWeeks,
    (monthlyCarRentalTotal, weeks) =>
        new Big(monthlyCarRentalTotal).times(weeks).div(AverageWeeksPerMonth).round(2, Big.roundHalfUp).toNumber()
);
export const selectMonthlyCarRentalHourlyTotal = createSelector(
    selectMonthlyCarRentalGrossTotal,
    selectPayTotalHours,
    (monthlyCarRentalTotal, totalHours) =>
        totalHours === 0 ? 0 : new Big(monthlyCarRentalTotal).div(totalHours).round(2, Big.roundHalfUp).toNumber()
);
export const selectOneTimeParkingGrossTotal = createSelector(
    selectParkingOneTime,
    (oneTimeCarRentalTotal) => oneTimeCarRentalTotal
);
export const selectOneTimeParkingHourlyTotal = createSelector(
    selectOneTimeParkingGrossTotal,
    selectPayTotalHours,
    (oneTimeParkingTotal, totalHours) =>
        totalHours === 0 ? 0 : new Big(oneTimeParkingTotal).div(totalHours).round(2, Big.roundHalfUp).toNumber()
);
export const selectMonthlyParkingGrossTotal = createSelector(
    selectParkingMonthly,
    selectWeeks,
    (monthlyParkingTotal, weeks) =>
        new Big(monthlyParkingTotal).times(weeks).div(AverageWeeksPerMonth).round(2, Big.roundHalfUp).toNumber()
);
export const selectMonthlyParkingHourlyTotal = createSelector(
    selectMonthlyParkingGrossTotal,
    selectPayTotalHours,
    (monthlyParkingTotal, totalHours) =>
        totalHours === 0 ? 0 : new Big(monthlyParkingTotal).div(totalHours).round(2, Big.roundHalfUp).toNumber()
);
export const selectFingerprintingGrossTotal = createSelector(
    selectFingerprintingOneTime,
    (fingerprintingTotal) => fingerprintingTotal
);
export const selectFingerprintingHourlyTotal = createSelector(
    selectFingerprintingGrossTotal,
    selectPayTotalHours,
    (fingerprintingTotal, totalHours) =>
        totalHours === 0 ? 0 : new Big(fingerprintingTotal).div(totalHours).round(2, Big.roundHalfUp).toNumber()
);
export const selectScrubsGrossTotal = createSelector(selectScrubsOneTime, (scrubsTotal) => scrubsTotal);
export const selectScrubsHourlyTotal = createSelector(
    selectScrubsGrossTotal,
    selectPayTotalHours,
    (scrubsTotal, totalHours) =>
        totalHours === 0 ? 0 : new Big(scrubsTotal).div(totalHours).round(2, Big.roundHalfUp).toNumber()
);
export const selectTaxableGrossTotal = createSelector(selectTaxableModulesOneTime, (taxableTotal) => taxableTotal);
export const selectTaxableHourlyTotal = createSelector(
    selectTaxableGrossTotal,
    selectPayTotalHours,
    (taxableTotal, totalHours) =>
        totalHours === 0 ? 0 : new Big(taxableTotal).div(totalHours).round(2, Big.roundHalfUp).toNumber()
);
export const selectOneTimeCustomReimbursementGrossTotal = createSelector(
    selectOneTimeCustomReimbursementAmount,
    (oneTimeCustomTotal) => oneTimeCustomTotal
);
export const selectOneTimeCustomReimbursementHourlyTotal = createSelector(
    selectOneTimeCustomReimbursementAmount,
    selectPayTotalHours,
    (oneTimeCustomTotal, totalHours) =>
        totalHours === 0 ? 0 : new Big(oneTimeCustomTotal).div(totalHours).round(2, Big.roundHalfUp).toNumber()
);
export const selectMonthlyCustomReimbursementGrossTotal = createSelector(
    selectMonthlyCustomReimbursementAmount,
    selectWeeks,
    (monthlyCustomTotal, weeks) =>
        new Big(monthlyCustomTotal).times(weeks).div(AverageWeeksPerMonth).round(2, Big.roundHalfUp).toNumber()
);
export const selectMonthlyCustomReimbursementHourlyTotal = createSelector(
    selectMonthlyCustomReimbursementGrossTotal,
    selectPayTotalHours,
    (monthlyCustomTotal, totalHours) =>
        totalHours === 0 ? 0 : new Big(monthlyCustomTotal).div(totalHours).round(2, Big.roundHalfUp).toNumber()
);

export const selectPickledAmountHourlyTotal = createSelector(
    selectPickledAmountTotal,
    selectPayTotalHours,
    (pickledAmountTotal, totalHours) =>
        totalHours === 0 ? 0 : new Big(pickledAmountTotal).div(totalHours).round(2, Big.roundHalfUp).toNumber()
);

export const selectReimbursementsHourlyTotal = createSelector(
    selectRelocationHourlyTotal,
    selectStateLicensuresHourlyTotal,
    selectEducationReimbursementsHourlyTotal,
    selectOneTimeCarRentalHourlyTotal,
    selectMonthlyCarRentalHourlyTotal,
    selectOneTimeParkingHourlyTotal,
    selectMonthlyParkingHourlyTotal,
    selectFingerprintingHourlyTotal,
    selectScrubsHourlyTotal,
    selectTaxableHourlyTotal,
    selectOneTimeCustomReimbursementHourlyTotal,
    selectMonthlyCustomReimbursementHourlyTotal,
    selectPickledAmountHourlyTotal,
    selectIsReimbursmentPickled,
    (
        relocationHourly,
        stateLicensuresHourly,
        educationReimbursementsHourly,
        oneTimeCarRentalHourlyTotal,
        monthlyCarRentalHourlyTotal,
        oneTimeParkingHourlyTotal,
        monthlyParkingHourlyTotal,
        fingerprintingHourlyTotal,
        scrubsHourlyTotal,
        taxableHourlyTotal,
        oneTimeCustomHourlyTotal,
        monthlyCustomHourlyTotal,
        pickledAmountHourlyTotal,
        isReimbursmentPickled
    ) =>
        isReimbursmentPickled
            ? new Big(relocationHourly)
                  .add(stateLicensuresHourly)
                  .add(educationReimbursementsHourly)
                  .add(oneTimeCarRentalHourlyTotal)
                  .add(monthlyCarRentalHourlyTotal)
                  .add(oneTimeParkingHourlyTotal)
                  .add(monthlyParkingHourlyTotal)
                  .add(fingerprintingHourlyTotal)
                  .add(scrubsHourlyTotal)
                  .add(taxableHourlyTotal)
                  .add(oneTimeCustomHourlyTotal)
                  .add(monthlyCustomHourlyTotal)
                  .sub(pickledAmountHourlyTotal)
                  .round(2, Big.roundHalfUp)
                  .toNumber()
            : new Big(relocationHourly)
                  .add(stateLicensuresHourly)
                  .add(educationReimbursementsHourly)
                  .add(oneTimeCarRentalHourlyTotal)
                  .add(monthlyCarRentalHourlyTotal)
                  .add(oneTimeParkingHourlyTotal)
                  .add(monthlyParkingHourlyTotal)
                  .add(fingerprintingHourlyTotal)
                  .add(scrubsHourlyTotal)
                  .add(taxableHourlyTotal)
                  .add(oneTimeCustomHourlyTotal)
                  .add(monthlyCustomHourlyTotal)
                  .round(2, Big.roundHalfUp)
                  .toNumber()
);
export const selectReimbursementsGrossTotal = createSelector(
    selectRelocationGrossTotal,
    selectStateLicensuresGrossTotal,
    selectEducationReimbursementsGrossTotal,
    selectOneTimeCarRentalGrossTotal,
    selectMonthlyCarRentalGrossTotal,
    selectOneTimeParkingGrossTotal,
    selectMonthlyParkingGrossTotal,
    selectFingerprintingGrossTotal,
    selectScrubsGrossTotal,
    selectTaxableGrossTotal,
    selectOneTimeCustomReimbursementGrossTotal,
    selectMonthlyCustomReimbursementGrossTotal,
    selectPickledAmountTotal,
    selectIsReimbursmentPickled,
    (
        relocationTotal,
        stateLicensuresGrossTotal,
        educationReimbursementsGrossTotal,
        oneTimeCarRentalGrossTotal,
        monthlyCarRentalGrossTotal,
        oneTimeParkingGrossTotal,
        monthlyParkingGrossTotal,
        fingerprintingGrossTotal,
        scrubsGrossTotal,
        taxableGrossTotal,
        oneTimeCustomGrossTotal,
        monthlyCustomGrossTotal,
        pickledAmount,
        isReimbursmentPickled
    ) =>
        isReimbursmentPickled
            ? new Big(relocationTotal)
                  .add(stateLicensuresGrossTotal)
                  .add(educationReimbursementsGrossTotal)
                  .add(oneTimeCarRentalGrossTotal)
                  .add(monthlyCarRentalGrossTotal)
                  .add(oneTimeParkingGrossTotal)
                  .add(monthlyParkingGrossTotal)
                  .add(fingerprintingGrossTotal)
                  .add(scrubsGrossTotal)
                  .add(taxableGrossTotal)
                  .add(oneTimeCustomGrossTotal)
                  .add(monthlyCustomGrossTotal)
                  .sub(pickledAmount)
                  .round(2, Big.roundHalfUp)
                  .toNumber()
            : new Big(relocationTotal)
                  .add(stateLicensuresGrossTotal)
                  .add(educationReimbursementsGrossTotal)
                  .add(oneTimeCarRentalGrossTotal)
                  .add(monthlyCarRentalGrossTotal)
                  .add(oneTimeParkingGrossTotal)
                  .add(monthlyParkingGrossTotal)
                  .add(fingerprintingGrossTotal)
                  .add(scrubsGrossTotal)
                  .add(taxableGrossTotal)
                  .add(oneTimeCustomGrossTotal)
                  .add(monthlyCustomGrossTotal)
                  .round(2, Big.roundHalfUp)
                  .toNumber()
);

export const selectReimbursementsCount = createSelector(
    selectRelocationGrossTotal,
    selectStateLicensuresGrossTotal,
    selectEducationReimbursementsGrossTotal,
    selectOneTimeCarRentalGrossTotal,
    selectMonthlyCarRentalGrossTotal,
    selectOneTimeParkingGrossTotal,
    selectMonthlyParkingGrossTotal,
    selectFingerprintingGrossTotal,
    selectScrubsGrossTotal,
    selectTaxableGrossTotal,
    selectOneTimeCustomReimbursementGrossTotal,
    selectMonthlyCustomReimbursementGrossTotal,
    selectPickledAmountTotal,
    (
        relocationTotal,
        stateLicensuresGrossTotal,
        educationReimbursementsGrossTotal,
        oneTimeCarRentalGrossTotal,
        monthlyCarRentalGrossTotal,
        oneTimeParkingGrossTotal,
        monthlyParkingGrossTotal,
        fingerprintingGrossTotal,
        scrubsGrossTotal,
        taxableGrossTotal,
        oneTimeCustomGrossTotal,
        monthlyCustomGrossTotal,
        pickledAmount
    ) =>
        [
            relocationTotal,
            stateLicensuresGrossTotal,
            educationReimbursementsGrossTotal,
            oneTimeCarRentalGrossTotal,
            monthlyCarRentalGrossTotal,
            oneTimeParkingGrossTotal,
            monthlyParkingGrossTotal,
            fingerprintingGrossTotal,
            scrubsGrossTotal,
            taxableGrossTotal,
            oneTimeCustomGrossTotal,
            monthlyCustomGrossTotal,
            pickledAmount
        ].filter((amount) => amount !== 0).length
);

export const selectBonusesFacilityPaidGross = createSelector(
    selectFacilityPaidBonus,
    (facilityPaidBonus) => facilityPaidBonus
);
export const selectBonusesFacilityPaidHourly = createSelector(
    selectBonusesFacilityPaidGross,
    selectPayTotalHours,
    (facilityPaidBonus, totalHours) =>
        totalHours === 0 ? 0 : new Big(facilityPaidBonus).div(totalHours).round(2, Big.roundHalfUp).toNumber()
);
export const selectBonusesDiscretionaryGross = createSelector(
    selectDiscretionaryBonus,
    (discretionaryBonus) => discretionaryBonus
);
export const selectBonusesDiscretionaryHourly = createSelector(
    selectBonusesDiscretionaryGross,
    selectPayTotalHours,
    (discretionaryTotal, totalHours) =>
        totalHours === 0 ? 0 : new Big(discretionaryTotal).div(totalHours).round(2, Big.roundHalfUp).toNumber()
);
export const selectAyaPaidBonusGross = createSelector(selectAyaPaidBonus, (ayaPaidBonus) => ayaPaidBonus);
export const selectAyaPaidBonusHourly = createSelector(
    selectAyaPaidBonusGross,
    selectPayTotalHours,
    (ayaPaidBonusTotal, totalHours) =>
        totalHours === 0 ? 0 : new Big(ayaPaidBonusTotal).div(totalHours).round(2, Big.roundHalfUp).toNumber()
);
export const selectBonusesReferralGross = createSelector(selectReferralBonus, (referralBonus) => referralBonus);
export const selectBonusesReferralHourly = createSelector(
    selectBonusesReferralGross,
    selectPayTotalHours,
    (referralBonus, totalHours) =>
        totalHours === 0 ? 0 : new Big(referralBonus).div(totalHours).round(2, Big.roundHalfUp).toNumber()
);
export const selectBonusesHourlyTotal = createSelector(
    selectAyaPaidBonusHourly,
    selectBonusesDiscretionaryHourly,
    selectBonusesReferralHourly,
    selectPickledAmountHourlyTotal,
    selectIsBonusPickled,
    (ayaPaidBonusHourly, discretionaryHourly, referralHourly, pickledAmountHourly, isBonusPickled) =>
        isBonusPickled
            ? new Big(discretionaryHourly)
                  .add(referralHourly)
                  .add(ayaPaidBonusHourly)
                  .sub(pickledAmountHourly)
                  .round(2, Big.roundHalfUp)
                  .toNumber()
            : new Big(discretionaryHourly)
                  .add(ayaPaidBonusHourly)
                  .add(referralHourly)
                  .round(2, Big.roundHalfUp)
                  .toNumber()
);
export const selectBonusesGrossTotal = createSelector(
    selectAyaPaidBonusGross,
    selectBonusesDiscretionaryGross,
    selectBonusesReferralGross,
    selectPickledAmountTotal,
    selectIsBonusPickled,
    (ayaPaidBonusTotal, discretionaryTotal, referralTotal, pickledAmountTotal, isBonusPickled) =>
        isBonusPickled
            ? new Big(discretionaryTotal)
                  .add(referralTotal)
                  .add(ayaPaidBonusTotal)
                  .sub(pickledAmountTotal)
                  .round(2, Big.roundHalfUp)
                  .toNumber()
            : new Big(discretionaryTotal).add(referralTotal).add(ayaPaidBonusTotal).round(2, Big.roundHalfUp).toNumber()
);
export const selectHousingMonthlyRentGross = createSelector(selectMonthlyRentPerContract, (monthlyRent) => monthlyRent);
export const selectHousingMonthlyRentHourly = createSelector(
    selectHousingMonthlyRentGross,
    selectPayTotalHours,
    (monthlyRent, totalHours) =>
        totalHours === 0 ? 0 : new Big(monthlyRent).div(totalHours).round(2, Big.roundHalfUp).toNumber()
);
export const selectHousingFurnitureAndUtilitiesGross = createSelector(
    selectMoveOutExpenses,
    selectFurnitureDelivery,
    selectFurniturePerContract,
    selectUtilitiesPerContract,
    (moveOutExpenses, furnitureDelivery, furniture, utilities) =>
        new Big(moveOutExpenses)
            .add(furnitureDelivery)
            .add(furniture)
            .add(utilities)
            .round(2, Big.roundHalfUp)
            .toNumber()
);
export const selectHousingFurnitureAndUtilitiesHourly = createSelector(
    selectHousingFurnitureAndUtilitiesGross,
    selectPayTotalHours,
    (furnitureAndUtilitiesGross, totalHours) =>
        totalHours === 0 ? 0 : new Big(furnitureAndUtilitiesGross).div(totalHours).round(2, Big.roundHalfUp).toNumber()
);
export const selectHousingHousewaresGross = createSelector(
    selectTotalHousewaresContractCost,
    (housewaresTotal) => housewaresTotal
);
export const selectHousingHousewaresHourly = createSelector(
    selectHousingHousewaresGross,
    selectPayTotalHours,
    (housewaresTotal, totalHours) =>
        totalHours === 0 ? 0 : new Big(housewaresTotal).div(totalHours).round(2, Big.roundHalfUp).toNumber()
);
export const selectHousingHourlyTotal = createSelector(
    selectHousingMonthlyRentHourly,
    selectHousingFurnitureAndUtilitiesHourly,
    selectHousingHousewaresHourly,
    (monthlyRentHourlyTotal, furnitureAndUtilitiesHourlyTotal, housewaresHourlyTotal) =>
        new Big(monthlyRentHourlyTotal)
            .add(furnitureAndUtilitiesHourlyTotal)
            .add(housewaresHourlyTotal)
            .round(2, Big.roundHalfUp)
            .toNumber()
);
export const selectHousingGrossTotal = createSelector(
    selectHousingMonthlyRentGross,
    selectHousingFurnitureAndUtilitiesGross,
    selectHousingHousewaresGross,
    (monthlyRentGrossTotal, furnitureAndUtilitiesGrossTotal, housewaresGrossTotal) =>
        new Big(monthlyRentGrossTotal)
            .add(furnitureAndUtilitiesGrossTotal)
            .add(housewaresGrossTotal)
            .round(2, Big.roundHalfUp)
            .toNumber()
);
export const selectMedicalInsuranceGrossTotal = createSelector(
    selectTotalMedicalCostCalculated,
    (medicalTotal) => medicalTotal
);
export const selectMedicalInsuranceHourlyTotal = createSelector(
    selectMedicalInsuranceGrossTotal,
    selectPayTotalHours,
    (medicalTotal, totalHours) =>
        totalHours === 0 ? 0 : new Big(medicalTotal).div(totalHours).round(2, Big.roundHalfUp).toNumber()
);
export const selectDentalVisionLifeInsuranceGrossTotal = createSelector(
    selectTotalDentalCostCalculated,
    (dentalVisionLifeTotal) => dentalVisionLifeTotal
);
export const selectDentalVisionLifeInsuranceHourlyTotal = createSelector(
    selectDentalVisionLifeInsuranceGrossTotal,
    selectPayTotalHours,
    (dentalVisionLifeTotal, totalHours) =>
        totalHours === 0 ? 0 : new Big(dentalVisionLifeTotal).div(totalHours).round(2, Big.roundHalfUp).toNumber()
);
export const selectInsuranceGrossTotal = createSelector(
    selectTotalInsuranceCostCalculated,
    (insuranceTotal) => insuranceTotal ?? 0
);
export const selectInsuranceHourlyTotal = createSelector(
    selectInsuranceGrossTotal,
    selectPayTotalHours,
    (insuranceTotal, totalHours) =>
        totalHours === 0 ? 0 : new Big(insuranceTotal).div(totalHours).round(2, Big.roundHalfUp).toNumber()
);
export const selectLabWorkGrossTotal = createSelector(selectLabWorkCosts, (labWorkTotal) => labWorkTotal ?? 0);
export const selectLabWorkHourlyTotal = createSelector(
    selectLabWorkGrossTotal,
    selectPayTotalHours,
    (labWorkTotal, totalHours) =>
        totalHours === 0 ? 0 : new Big(labWorkTotal).div(totalHours).round(2, Big.roundHalfUp).toNumber()
);
export const selectEmployerHourlyTotal = createSelector(selectPayHourlyTotal, (payHourlyTotal) => payHourlyTotal);
export const selectEmployerGrossTotal = createSelector(selectPayGrossTotal, (payGrossTotal) => payGrossTotal);

/** Tax multiplier for the employer. Based on the facility state */
export const selectEmployerGrossTaxMultiplier = createSelector(selectFacilityState, (state: string) =>
    !state ? 0 : MarginCalculatorPayBillingHelperService.determineMultiplier(state)
);
/** Taxable Stipends Amount */
export const selectTaxableStipendsAmount = createSelector(
    selectTaxableStipend,
    selectMealStipendsGrossTotal,
    selectHousingGrossTotal,
    (taxableStipend, mealStipend, housingStipend) => (taxableStipend === 1 ? mealStipend + housingStipend : 0)
);

/** Select the bonus pickled value if any bonus field is pickled */
export const selectBonusPickleValue = createSelector(
    selectIsDiscretionaryPickled,
    selectDiscretionaryBonus,
    selectIsReferralPickled,
    selectReferralBonus,
    (isDiscretionaryPickled, discretionaryBonus, isReferralPickled, referralBonus) =>
        isDiscretionaryPickled ? discretionaryBonus : isReferralPickled ? referralBonus : 0
);

export const selectTaxableModuleValue = createSelector(
    selectIsTaxableModulePickled,
    selectTaxableModulesOneTime,
    (pickled, value) => (pickled ? 0 : value)
);

export const selectDiscretionReferralTotal = createSelector(
    selectDiscretionaryBonus,
    selectAyaPaidBonus,
    selectReferralBonus,
    selectBonusPickleValue,
    selectTaxableModuleValue,
    (discretion, ayaPaidBonus, referral, pickledBonus, taxableOneTime) => {
        const referralTotal = new Big(discretion)
            .add(ayaPaidBonus)
            .add(referral)
            .sub(pickledBonus)
            .sub(taxableOneTime)
            .toNumber();
        return referralTotal > 0 ? referralTotal : 0;
    }
);

export const selectEmployerTaxesGross = createSelector(
    selectPayGrossTotal,
    selectDiscretionReferralTotal,
    selectTaxableStipendsAmount,
    selectTaxableModuleValue,
    selectEmployerGrossTaxMultiplier,
    (grossTotal, discReferralTotal, taxableStipends, taxableModuleValue, employerGrossTaxMultiplier) =>
        new Big(grossTotal)
            .add(discReferralTotal)
            .add(taxableStipends)
            .add(taxableModuleValue)
            .times(employerGrossTaxMultiplier)
            .round(2, Big.roundHalfUp)
            .toNumber()
);

export const selectHourlyEmployerTaxes = createSelector(
    selectEmployerTaxesGross,
    selectPayTotalHours,
    (employerTaxesGross, payTotalHours) => {
        return payTotalHours !== 0
            ? new Big(employerTaxesGross).div(payTotalHours).round(2, Big.roundHalfUp).toNumber()
            : 0;
    }
);

export const selectCompensationGrossTotal = createSelector(
    selectPayGrossTotal,
    selectHousingStipendsGrossTotal,
    selectMealStipendsGrossTotal,
    selectReimbursementsGrossTotal,
    selectBonusesGrossTotal,
    selectHousingGrossTotal,
    selectInsuranceGrossTotal,
    selectLabWorkGrossTotal,
    selectEmployerTaxesGross,
    (
        payGrossTotal,
        housingStipendsGrossTotal,
        mealStipendsGrossTotal,
        reimbursementsGrossTotal,
        bonusesGrossTotal,
        housingGrossTotal,
        insuranceGrossTotal,
        labWorkGrossTotal,
        employerTaxesGross
    ) =>
        new Big(payGrossTotal)
            .add(housingStipendsGrossTotal)
            .add(mealStipendsGrossTotal)
            .add(reimbursementsGrossTotal)
            .add(bonusesGrossTotal)
            .add(housingGrossTotal)
            .add(insuranceGrossTotal)
            .add(labWorkGrossTotal)
            .add(employerTaxesGross)
            .round(2, Big.roundHalfUp)
            .toNumber()
);

export const selectCompensationHourlyTotal = createSelector(
    selectCompensationGrossTotal,
    selectBookingWeeks,
    selectShifts,
    selectShiftHours,
    selectAltBookingWeeks,
    selectAltShifts,
    selectAltShiftHours,
    selectIsRotatingShift,
    selectIsAlternatingShift,
    (
        compensationGrossTotal,
        bookingWeeks,
        numOfShifts,
        shiftLength,
        altBookingWeeks,
        altNumOfShifts,
        altShiftLength,
        isRotatingShift,
        isAlternatingShift
    ) => {
        if (isRotatingShift) {
            const amt = new Big(numOfShifts)
                .times(shiftLength)
                .times(bookingWeeks)
                .plus(new Big(numOfShifts).times(shiftLength).times(altBookingWeeks));
            return amt.gt(0) ? new Big(compensationGrossTotal).div(amt).round(2, Big.roundHalfUp).toNumber() : 0;
        }

        if (isAlternatingShift) {
            const amt = new Big(numOfShifts)
                .times(shiftLength)
                .times(bookingWeeks)
                .plus(new Big(altNumOfShifts).times(altShiftLength).times(altBookingWeeks));
            return amt.gt(0) ? new Big(compensationGrossTotal).div(amt).round(2, Big.roundHalfUp).toNumber() : 0;
        }

        const period = new Big(bookingWeeks).times(numOfShifts).times(shiftLength);
        return period.gt(0) ? new Big(compensationGrossTotal)?.div(period).round(2, Big.roundHalfUp).toNumber() : 0;
    }
);

/**********************************************************
 * Calculations - Additional Costs
 **********************************************************/

export const selectAdditionalCostsAyaBooster = createSelector(selectAyaBooster, (ayaBooster) => ayaBooster);
export const selectAdditionalCostsInternalState = createSelector(
    selectFpAmountInternal,
    (internalStateCosts) => internalStateCosts
);
export const selectAdditionalCostsInternal = createSelector(selectInternalCosts, (internalCosts) => internalCosts);
export const selectAdditionalCostsGrossTotal = createSelector(
    selectAdditionalCosts,
    selectLabWorkCosts,
    (additionalCosts: number, labWorkCosts: number) =>
        new Big(additionalCosts).minus(labWorkCosts).round(2, Big.roundHalfUp).toNumber()
);

/**********************************************************
 * Calculations - Facility Fees
 **********************************************************/

export const selectAdjustedRegularRate = createSelector(
    selectBaseBillingRateValue,
    selectAdjustedContractFees,
    (regBillRate, paybackPercent) => new Big(regBillRate).times(paybackPercent).toNumber()
);

export const selectAdjustedOvertimeRate = createSelector(
    selectOverTimeRate,
    selectAdjustedContractFees,
    (overtimeBillRate, paybackPercent) => new Big(overtimeBillRate).times(paybackPercent).toNumber()
);

export const selectTotalOrientationHours = createSelector(
    selectFirstOrientationHours,
    selectNextOrientationHours,
    (orientationHours, orientationNextAmount) => new Big(orientationHours).add(orientationNextAmount).toNumber()
);

export const selectRegularBillableHours = createSelector(
    selectBillingRegularHours,
    selectTotalOrientationHours,
    (regHours, orientationHours) => new Big(regHours).minus(orientationHours).toNumber()
);

export const selectTotalRegularBillableHours = createSelector(
    selectBillingRegularHours,
    (regularHours) => regularHours
);

export const selectAdjustedRegularPay = createSelector(
    selectAdjustedRegularRate,
    selectRegularPayHours,
    selectAltRegularPayHours,
    (adjustedRegularRate, totalRegularHours, totalAltRegularPayHours) =>
        new Big(adjustedRegularRate).times(Big(totalRegularHours).plus(totalAltRegularPayHours)).toNumber()
);

export const selectAdjustedOvertimePay = createSelector(
    selectAdjustedOvertimeRate,
    selectOvertimePayHours,
    selectAltOvertimePayHours,
    (adjustedOvertimeRate, totalOvertimeHours, altOvertimeHours) =>
        new Big(adjustedOvertimeRate).times(Big(totalOvertimeHours).plus(altOvertimeHours)).toNumber()
);

export const selectFacilityFeesContract = createSelector(
    selectAdjustedRegularPay,
    selectAdjustedOvertimePay,
    (adjustedRegularPay, adjustedOvertimePay) =>
        new Big(adjustedRegularPay).add(adjustedOvertimePay).round(2, Big.roundHalfUp).toNumber()
);

export const selectAdjustedEarlyPaymentRegularRate = createSelector(
    selectBaseBillingRateValue,
    selectMarginDiscountPercentDecimal,
    (regBillRate, discountPercentDecimal) => new Big(regBillRate).times(discountPercentDecimal).toNumber()
);

export const selectAdjustedEarlyPaymentOvertimeRate = createSelector(
    selectOverTimeRate,
    selectMarginDiscountPercentDecimal,
    (overtimeBillRate, marginDiscountPercentDecimal) =>
        new Big(overtimeBillRate).times(marginDiscountPercentDecimal).toNumber()
);

export const selectAdjustedEarlyPaymentRegularPay = createSelector(
    selectAdjustedEarlyPaymentRegularRate,
    selectRegularPayHours,
    selectAltRegularPayHours,
    (adjustedRegularRate, totalRegularHours, totalAltRegularPayHours) =>
        new Big(adjustedRegularRate).times(Big(totalRegularHours).plus(totalAltRegularPayHours)).toNumber()
);
export const selectAdjustedEarlyPaymentOvertimePay = createSelector(
    selectAdjustedEarlyPaymentOvertimeRate,
    selectOvertimePayHours,
    selectAltOvertimePayHours,
    (adjustedOvertimeRate, totalOvertimeHours, altOvertimeHours) =>
        new Big(adjustedOvertimeRate).times(Big(totalOvertimeHours).plus(altOvertimeHours)).toNumber()
);

export const selectFacilityFeesEarlyPayment = createSelector(
    selectAdjustedEarlyPaymentRegularPay,
    selectAdjustedEarlyPaymentOvertimePay,
    (adjustedRegularPay, adjustedOvertimePay) => {
        return new Big(adjustedRegularPay).add(adjustedOvertimePay).round(2, Big.roundHalfUp).toNumber();
    }
);

export const selectFacilityFeesExtension = createSelector(
    selectIsExtension,
    selectExtensionFees,
    (isExtension, extDiscount) => (isExtension ? extDiscount : 0)
);

/**********************************************************
 * Contract Info Specialized Selectors
 **********************************************************/
//#region Contract Info
export const selectGetContractLengthParams = createSelector(
    selectShiftType,
    selectStartDateAsDate,
    selectEndDateAsDate,
    selectVacationDatesAsDates,
    selectShifts,
    selectPendingFacilityApproval,
    (shiftType, startDate, endDate, vacationDates, shifts, pendingApproval) => ({
        shiftType,
        startDate,
        endDate,
        vacationDates,
        shifts,
        pendingApproval
    })
);

export const selectPayHoursParams = createSelector(
    selectShiftHours,
    selectAltShiftHours,
    selectShifts,
    selectAltShifts,
    selectFacilityState,
    selectShiftType,
    selectBookingWeeks,
    selectAltBookingWeeks,
    (shiftHours, altShiftHours, shifts, altShifts, facilityState, shiftType, bookingWeeks, altBookingWeeks) => ({
        shiftHours,
        altShiftHours,
        shifts,
        altShifts,
        facilityState,
        shiftType,
        bookingWeeks,
        altBookingWeeks
    })
);

export const selectSetBillableHoursParams = createSelector(
    selectStartDateAsDate,
    selectEndDateAsDate,
    selectVacationDatesAsDates,
    selectShiftHours,
    selectAltShiftHours,
    selectShifts,
    selectAltShifts,
    selectBookingWeeks,
    selectAltBookingWeeks,
    selectShiftType,
    selectBillingOvertimeEnabled,
    selectFirstOvertimeHours,
    selectSecondOvertimeHours,
    selectFirstOvertimeFrequency,
    selectSecondOvertimeFrequency,
    selectAndOrBillingRule,
    selectBillingRates,
    selectTotalOrientationHours,
    selectPendingFacilityApproval,
    (
        startDate,
        endDate,
        vacationDates,
        shiftHours,
        altShiftHours,
        shifts,
        altShifts,
        bookingWeeks,
        altBookingWeeks,
        shiftType,
        otBillingRulesEnabled,
        otFirstHour,
        otSecondHour,
        otFirstFrequency,
        otSecondFrequency,
        otAndOr,
        billingRates,
        orientationHours,
        pendingApproval
    ) => ({
        startDate,
        endDate,
        vacationDates,
        shiftHours,
        altShiftHours,
        shifts,
        altShifts,
        bookingWeeks,
        altBookingWeeks,
        shiftType,
        otBillingRulesEnabled,
        otFirstHour,
        otSecondHour,
        otFirstFrequency,
        otSecondFrequency,
        otAndOr,
        billingRates,
        orientationHours,
        pendingApproval
    })
);

export const selectExpectedBillingOvertimeEnabled = createSelector(
    selectShifts,
    selectAltShifts,
    selectFirstBillRate,
    selectIsAlternatingShift,
    selectShiftHours,
    selectAltShiftHours,
    selectFacilityFirstOvertimeFrequency,
    selectFacilitySecondOvertimeFrequency,
    selectFacilityFirstOvertimeHours,
    selectFacilityAndOrRule,
    (
        shifts,
        altShifts,
        billingRate,
        isAlternatingShift,
        shiftHours,
        altShiftHours,
        otFirstFrequency,
        otSecondFrequency,
        otFirstHours,
        otAndOr
    ) =>
        PayBillingStateHelpers.shouldEnableOvertimeBillingRule(
            isAlternatingShift,
            shifts,
            altShifts,
            shiftHours,
            altShiftHours,
            otFirstFrequency,
            otSecondFrequency,
            otAndOr,
            otFirstHours,
            billingRate
        )
);

export const selectOvertimeBillingRuleCheckParams = createSelector(
    selectShifts,
    selectAltShifts,
    selectBillingRates,
    selectIsAlternatingShift,
    selectShiftHours,
    selectAltShiftHours,
    selectFacilityFirstOvertimeFrequency,
    selectFacilitySecondOvertimeFrequency,
    selectFacilityFirstOvertimeHours,
    selectFacilityAndOrRule,
    (
        shifts,
        altShifts,
        billingRates,
        isAlternatingShift,
        shiftHours,
        altShiftHours,
        otFirstFrequency,
        otSecondFrequency,
        otFirstHours,
        otAndOr
    ) => ({
        shifts,
        altShifts,
        billingRates,
        isAlternatingShift,
        shiftHours,
        altShiftHours,
        otFirstFrequency,
        otSecondFrequency,
        otFirstHours,
        otAndOr
    })
);

export const selectGsaRateParams = createSelector(
    selectFacilityId,
    selectStartDateAsDate,
    selectEndDateAsDate,
    (facilityId, startDate, endDate) => ({
        facilityId,
        startDate,
        endDate
    })
);

export const selectWeeklyHoursParams = createSelector(selectShifts, selectShiftHours, (shifts, shiftHours) => ({
    shifts,
    shiftHours
}));

export const selectMaxStipendParams = createSelector(selectIsAyaHousing, selectGsaRates, (isAyaHousing, gsaRates) => ({
    isAyaHousing,
    gsaRates
}));

export const selectPremiumMargin = createSelector(selectMargin, (state: MarginState) => state.premiumMargin ?? 0);

export const selectContractRequestId = createSelector(
    selectMarginCalculatorState,
    (state: MarginCalculatorState) => state.contractRequestId
);
//#endregion

/**********************************************************
 * Calculations - Facility Billing - Billable Regular
 **********************************************************/

export const selectAdjustedBillRate = createSelector(
    selectBaseBillingRateValue,
    selectIsExtension,
    selectAdjustedContractFees,
    selectFacilityExtDiscount,
    selectMarginDiscountPercentDecimal,
    selectFacilityExtDiscountType,
    (billingRate, isExtension, payBackPercent, extDiscount, discountPercentDecimal, extDiscountType) => {
        return PayBillingStateHelpers.calculateAdjustedBillRate(
            isExtension,
            billingRate,
            payBackPercent,
            extDiscount,
            discountPercentDecimal,
            extDiscountType
        );
    }
);
export const selectAltAdjustedBillRate = createSelector(
    selectBaseBillingRateAltValue,
    selectIsExtension,
    selectAdjustedContractFees,
    selectFacilityExtDiscount,
    selectMarginDiscountPercentDecimal,
    selectFacilityExtDiscountType,
    (billingRateAlt, isExtension, payBackPercent, extDiscount, discountPercentDecimal, extDiscountType) => {
        return PayBillingStateHelpers.calculateAdjustedBillRate(
            isExtension,
            billingRateAlt,
            payBackPercent,
            extDiscount,
            discountPercentDecimal,
            extDiscountType
        );
    }
);
export const selectOrientationParams = createSelector(
    selectOrientationAllHoursBillable,
    selectOrientationType,
    selectFirstOrientationHours,
    selectFirstOrientationAmount,
    selectOrientationNextType,
    selectNextOrientationHours,
    selectNextOrientationAmount,
    (allHoursBillable, orientType, orientHours, orientRate, orientTypeAlt, orientHoursAlt, orientRateAlt) => {
        return {
            allHoursBillable,
            orientType,
            orientHours,
            orientRate,
            orientTypeAlt,
            orientHoursAlt,
            orientRateAlt
        } satisfies OrientationModel;
    }
);
export const selectTotalOrientationAmount = createSelector(
    selectAdjustedContractFees,
    selectMarginExtDiscount,
    selectMarginDiscountPercentDecimal,
    selectIsExtension,
    selectFacility,
    selectOrientationParams,
    selectAdjustedBillRate,
    (
        payBackPercent,
        extDiscount,
        marginDiscountPercentDecimal,
        isExtension,
        facility,
        orientationParams,
        adjustedBillRate
    ) =>
        PayBillingStateHelpers.calculateTotalOrientation(
            payBackPercent,
            new Big(extDiscount).div(100).toNumber(),
            marginDiscountPercentDecimal,
            isExtension,
            facility,
            orientationParams,
            adjustedBillRate
        )
);
export const selectPrimaryNonStandardOvertimeBillingHours = createSelector(
    selectMargin,
    (margin) => margin.primaryNonStandardOvertimeBillingHours ?? 0
);
export const selectAlternateNonStandardOvertimeBillingHours = createSelector(
    selectMargin,
    (margin) => margin.alternateNonStandardOvertimeBillingHours ?? 0
);

export const selectCalculatedRegularBillingHour = createSelector(
    selectBookingWeeks,
    selectShifts,
    selectShiftHours,
    selectPrimaryNonStandardOvertimeBillingHours,
    (weeks, shifts, shiftLength, otHrs) => FormulaHelperService.calculateBillingHour(weeks, shifts, shiftLength, otHrs)
);

export const selectCalculatedAltBillingHour = createSelector(
    selectAltBookingWeeks,
    selectAltShifts,
    selectAltShiftHours,
    selectAlternateNonStandardOvertimeBillingHours,
    selectShifts,
    selectShiftHours,
    selectIsRotatingShift,
    (weeks, shifts, shiftLength, otHrs, regShifts, regShiftLength, isRotating) => {
        if (isRotating) {
            shifts = regShifts;
            shiftLength = regShiftLength;
        }
        return FormulaHelperService.calculateBillingHour(weeks, shifts, shiftLength, otHrs);
    }
);

export const selectStandardBillableRegularTotal = createSelector(
    selectRegularBillableHours,
    selectAdjustedBillRate,
    selectTotalOrientationAmount,
    (regularBillableHours, adjustedBillRate, orientationBillTotal) =>
        new Big(regularBillableHours)
            .times(adjustedBillRate)
            .plus(orientationBillTotal)
            .round(2, Big.roundHalfUp)
            .toNumber()
);
export const selectBillableRegularTotal = createSelector(selectStandardBillableRegularTotal, (total) => total);

export const selectNonStandardBillableRegularTotal = createSelector(
    selectCalculatedRegularBillingHour,
    selectTotalOrientationHours,
    selectAdjustedBillRate,
    selectTotalOrientationAmount,
    selectCalculatedAltBillingHour,
    selectAltAdjustedBillRate,
    (
        primaryBillableHours,
        totalOrientationHours,
        adjustedBillRate,
        orientationBillTotal,
        altBillingHours,
        altAdjustedBillRate
    ) =>
        new Big(Big(primaryBillableHours).minus(totalOrientationHours))
            .times(adjustedBillRate)
            .plus(orientationBillTotal)
            .plus(Big(altBillingHours).times(altAdjustedBillRate))
            .round(2, Big.roundHalfUp)
            .toNumber()
);
/**********************************************************
 * Calculations - Facility Billing - Billable OT
 **********************************************************/

export const selectAdjustedOtBillRate = createSelector(
    selectOverTimeRate,
    selectIsExtension,
    selectAdjustedContractFees,
    selectFacilityExtDiscount,
    selectMarginDiscountPercentDecimal,
    selectFacilityExtDiscountType,
    (overtimeRate, isExtension, payBackPercent, extDiscount, discountPercentDecimal, extDiscountType) => {
        return PayBillingStateHelpers.calculateAdjustedBillRate(
            isExtension,
            overtimeRate,
            payBackPercent,
            extDiscount,
            discountPercentDecimal,
            extDiscountType
        );
    }
);
export const selectAltAdjustedOtBillRate = createSelector(
    selectOverTimeRateAlt,
    selectIsExtension,
    selectAdjustedContractFees,
    selectFacilityExtDiscount,
    selectMarginDiscountPercentDecimal,
    selectFacilityExtDiscountType,
    (overtimeRateAlt, isExtension, payBackPercent, extDiscount, discountPercentDecimal, extDiscountType) => {
        return PayBillingStateHelpers.calculateAdjustedBillRate(
            isExtension,
            overtimeRateAlt,
            payBackPercent,
            extDiscount,
            discountPercentDecimal,
            extDiscountType
        );
    }
);
export const selectStandardBillableOvertimeTotal = createSelector(
    selectBillingOvertimeHours,
    selectAdjustedOtBillRate,
    (overtimeBillableHours, adjustedOtBillRate) =>
        new Big(new Big(overtimeBillableHours).times(adjustedOtBillRate)).round(2, Big.roundHalfUp).toNumber()
);

export const selectNonStandardBillableOvertimeTotal = createSelector(
    selectPrimaryNonStandardOvertimeBillingHours,
    selectAlternateNonStandardOvertimeBillingHours,
    selectAdjustedOtBillRate,
    selectAltAdjustedOtBillRate,
    (overtimeBillableHours, altOvertimePayHours, adjustedOtBillRate, altAdjustedOtBillRate) =>
        new Big(new Big(overtimeBillableHours).times(adjustedOtBillRate))
            .plus(new Big(altOvertimePayHours).times(altAdjustedOtBillRate))
            .round(2, Big.roundHalfUp)
            .toNumber()
);
export const selectBillableOvertimeTotal = createSelector(
    selectBillingOvertimeEnabled,
    selectBillingOvertimeHours,
    selectIsStandardShift,
    selectStandardBillableOvertimeTotal,
    selectNonStandardBillableOvertimeTotal,
    (isBillOverTime, billingOverTimeHours, isStandardShift, standardOTtotal, nonStandardOtTotal) =>
        isBillOverTime && billingOverTimeHours > 0 ? (isStandardShift ? standardOTtotal : nonStandardOtTotal) : 0
);

/**********************************************************
 * Calculations - General
 **********************************************************/

export const selectExpenseTotal = createSelector(
    selectAdditionalCostsGrossTotal,
    selectCompensationGrossTotal,
    (additionalCostsTotal, compensationTotal) =>
        new Big(additionalCostsTotal).add(compensationTotal).neg().round(2, Big.roundHalfUp).toNumber()
);
export const selectBillableTotal = createSelector(
    selectStandardBillableRegularTotal,
    selectNonStandardBillableRegularTotal,
    selectBillableOvertimeTotal,
    selectIsStandardShift,
    (standardregularTotal, nonStandardregularTotal, overTimeTotal, isStandardShift) => {
        const regularTotal: number = isStandardShift ? standardregularTotal : nonStandardregularTotal;
        return new Big(regularTotal).add(overTimeTotal).round(2, Big.roundHalfUp).toNumber();
    }
);
export const selectProfit = createSelector(selectBillableTotal, selectExpenseTotal, (billableTotal, expenseTotal) =>
    new Big(billableTotal).add(expenseTotal).round(2, Big.roundHalfUp).toNumber()
);
export const selectActualMargin = createSelector(selectProfit, selectBillableTotal, (profit, billableTotal) =>
    billableTotal === 0 ? 0 : new Big(profit).div(billableTotal).toNumber()
);
export const selectCommissionMultiplier = createSelector(selectActualMargin, (actualMargin) => {
    if (actualMargin > 0.3) {
        return 0.12;
    }
    if (actualMargin >= 0.29) {
        return 0.1;
    }
    if (actualMargin >= 0.28) {
        return 0.095;
    }
    if (actualMargin >= 0.27) {
        return 0.09;
    }
    if (actualMargin >= 0.26) {
        return 0.085;
    }
    if (actualMargin >= 0.25) {
        return 0.08;
    }
    if (actualMargin >= 0.24) {
        return 0.075;
    }
    if (actualMargin >= 0.23) {
        return 0.07;
    }
    if (actualMargin >= 0.225) {
        return 0.065;
    }
    return 0.06;
});
export const selectContractCommission = createSelector(
    selectProfit,
    selectCommissionMultiplier,
    (profit, commissionMultiplier) => new Big(profit).times(commissionMultiplier).round(2, Big.roundHalfUp).toNumber()
);
export const selectRecruiterHourlyBaseRate = createSelector(
    selectContractCommission,
    selectPayTotalHours,
    (contractCommission, payHours) =>
        payHours === 0 ? 0 : new Big(contractCommission).div(payHours).round(2, Big.roundHalfUp).toNumber()
);

export const selectReviewAndSendEnabled = createSelector(
    selectMarginCalculatorState,
    (state: MarginCalculatorState) => state?.reviewAndSendEnabled ?? false
);

export const selectContinueWithoutLinking = createSelector(
    selectMarginCalculatorState,
    (state: MarginCalculatorState) => state?.continueWithoutLinking ?? false
);

export const selectFixRedBannerReadonlyIssueFlag = createSelector(
    selectFeatureFlags,
    (featureFlags: FeatureFlagState) =>
        (featureFlags && featureFlags[PathfindersFlags.FixRedBannerReadonlyIssue]) ?? false
);

export const selectMarginFormDisabled = createSelector(
    selectMarginExists,
    selectUserId,
    selectCreatedBy,
    selectIsNurseContracting,
    selectIsLinkedContractRequestInDraftingStatus,
    selectIsPipelined,
    selectLoadedMargin,
    selectFixRedBannerReadonlyIssueFlag,
    (
        marginExists: boolean,
        userId: number,
        createdBy: number,
        isNurseContracting: boolean,
        isLinkedContractRequestInDraftingStatus: boolean,
        isPipelined: boolean,
        loadedMargin: MarginState,
        fixRedBannerReadonlyIssueFlag: boolean
    ) => {
        if (!marginExists) {
            return false;
        }

        if (isPipelined) {
            return true;
        }

        return (
            !isNurseContracting &&
            (userId !== createdBy ||
                isLinkedContractRequestInDraftingStatus ||
                (fixRedBannerReadonlyIssueFlag && !!loadedMargin?.createdBy && userId !== loadedMargin?.createdBy))
        );
    }
);

/**********************************************************
 * Vacation dates
 **********************************************************/

export const selectTotalVacationDays = createSelector(selectVacationDatesAsDates, (vacationDates) => {
    return vacationDates.length;
});

export const selectVacationDaysRequested = createSelector(selectVacationDatesAsDates, (vacationDates) => {
    return MarginCalculatorStateHelpers.combinedDateRangeDisplay(vacationDates);
});

//#endregion

/**********************************************************
 * Banner
 **********************************************************/
//#region Banner
export const selectTargetMargin = createSelector(selectMargin, (margin: MarginState) => margin?.targetMargin ?? 0);

export const selectActualMarginColor = createSelector(
    selectActualMargin,
    selectTargetMargin,
    (actualMargin: number, targetMargin: number) => {
        const actualMarginCalc = new Big(actualMargin).times(100).round(2, Big.roundHalfUp).toNumber();
        if (actualMarginCalc >= targetMargin) {
            return 'actual-margin--green';
        }
        const minimumTarget = new Big(targetMargin).minus(3).toNumber();
        if (actualMarginCalc >= minimumTarget && actualMarginCalc <= targetMargin) {
            return 'actual-margin--orange';
        }
        return 'actual-margin--red';
    }
);

export const selectEstTaxRate = createSelector(selectMargin, (margin: MarginState) => margin?.taxRate ?? 0);

/** Select default Overtime PayRate for the state */
export const selectOtPay = createSelector(
    selectFacilityState,
    selectMarginPayRate,
    selectPremiumRate,
    (facilityState, basePayRate, otPayRate) =>
        MarginCalculatorPayBillingHelperService.calculateOTRate(facilityState, basePayRate, otPayRate)
);

export const selectEstNetWeekly = createSelector(
    selectOtPay,
    selectMarginPayRate,
    selectRegularPayHours,
    selectWeeklyOvertimePayHours,
    selectEstTaxRate,
    selectStipendsTotal,
    selectBookingWeeks,
    (otPay, basePayRate, regPayHrs, otWeeklyPayHrs, taxRate, stipendsTotal, bookingWeeks) => {
        const regWeeklyHours = bookingWeeks > 0 ? new Big(regPayHrs).div(new Big(bookingWeeks)) : 0;
        return new Big(basePayRate)
            .times(regWeeklyHours)
            .plus(new Big(otPay).times(new Big(otWeeklyPayHrs)))
            .times(new Big(1).minus(new Big(taxRate).div(100)))
            .plus(stipendsTotal)
            .round(2, Big.roundHalfUp)
            .toNumber();
    }
);

export const selectEstAltNetWeekly = createSelector(
    selectOtPay,
    selectMarginPayRate,
    selectWeeklyAltRegularPayHours,
    selectWeeklyAltOvertimePayHours,
    selectEstTaxRate,
    selectWeeklyStipends,
    selectIsAlternatingShift,
    (otPay, basePayRate, weeklyRegAltPayHrs, weeklyOtAltPayHrs, taxRate, weeklyStipend, isAlternatingShift) => {
        if (isAlternatingShift) {
            return new Big(basePayRate)
                .times(weeklyRegAltPayHrs)
                .plus(new Big(otPay).times(weeklyOtAltPayHrs))
                .times(new Big(1).minus(new Big(taxRate).div(100)))
                .plus(weeklyStipend)
                .round(2, Big.roundHalfUp)
                .toNumber();
        }
        return 0;
    }
);

export const selectCompensationWeeklyTotal = createSelector(
    selectCompensationGrossTotal,
    selectBookingWeeks,
    selectAltBookingWeeks,
    (compensationGrossTotal: number, selectWeeks: number, selectAltWeeks: number) =>
        selectWeeks > 0
            ? new Big(compensationGrossTotal)
                  .div(selectWeeks + selectAltWeeks)
                  .round(2, Big.roundHalfUp)
                  .toNumber()
            : 0
);

export const selectWeeklyGrossPayTotal = createSelector(
    selectMarginPayRate,
    selectOtPay,
    selectStipendsTotal,
    selectRegularPayHours,
    selectBookingWeeks,
    selectWeeklyOvertimePayHours,
    (basePayRate, otRate, stipendsTotal, regPayHrs, bookingWeeks, otWeeklyPayHrs) => {
        const regWeeklyHrs = bookingWeeks > 0 ? new Big(regPayHrs).div(bookingWeeks) : 0;
        return new Big(basePayRate)
            .times(regWeeklyHrs)
            .plus(new Big(otRate).times(otWeeklyPayHrs))
            .plus(stipendsTotal)
            .round(2, Big.roundHalfUp)
            .toNumber();
    }
);

export const selectAltWeeklyGrossPayTotal = createSelector(
    selectMarginPayRate,
    selectWeeklyAltRegularPayHours,
    selectOtPay,
    selectWeeklyAltOvertimePayHours,
    selectWeeklyStipends,
    (basePayRate, regPayHrs, otRate, otPayHrs, weeklyStipend) =>
        new Big(basePayRate)
            .times(new Big(regPayHrs))
            .plus(new Big(otRate).times(otPayHrs))
            .plus(weeklyStipend)
            .round(2, Big.roundHalfUp)
            .toNumber()
);

//#endregion

/**********************************************************
 * pay package
 **********************************************************/
export const selectTimeFrame = createSelector(
    selectStartTimeFrameHours,
    selectStartTimeFrameMinutes,
    selectEndTimeFrameHours,
    selectEndTimeFrameMinutes,
    (timeFrameStartHours, timeFrameStartMinutes, timeFrameEndHours, timeFrameEndMinutes) => {
        return `${timeFrameStartHours}:${MarginCalculatorStateHelpers.correctMinutes(
            timeFrameStartMinutes
        )} - ${timeFrameEndHours}:${MarginCalculatorStateHelpers.correctMinutes(timeFrameEndMinutes)}`;
    }
);

export const selectAltTimeFrame = createSelector(
    selectAltStartTimeFrameHours,
    selectAltStartTimeFrameMinutes,
    selectAltEndTimeFrameHours,
    selectAltEndTimeFrameMinutes,
    (timeFrameStartHours, timeFrameStartMinutes, timeFrameEndHours, timeFrameEndMinutes) => {
        return `${timeFrameStartHours}:${MarginCalculatorStateHelpers.correctMinutes(
            timeFrameStartMinutes
        )} - ${timeFrameEndHours}:${MarginCalculatorStateHelpers.correctMinutes(timeFrameEndMinutes)}`;
    }
);

export const selectTotalHoursPerWeek = createSelector(
    selectShifts,
    selectShiftHours,
    (shifts: number, shiftHours: number) => {
        return new Big(shiftHours).times(shifts).toNumber();
    }
);

export const selectAltTotalHoursPerWeek = createSelector(
    selectAltShifts,
    selectAltShiftHours,
    selectIsAlternatingShift,
    (shifts: number, shiftHours: number, alternating: boolean) =>
        alternating ? new Big(shiftHours).times(shifts).toNumber() : 0
);

export const selectIsCA = createSelector(selectFacilityState, (state) => {
    return state === 'CA';
});
export const selectIsVI = createSelector(selectFacilityState, (state) => {
    return state === 'VI';
});
export const selectIsAK = createSelector(selectFacilityState, (state) => {
    return state === 'AK';
});
export const selectIsCaViAk = createSelector(selectFacilityState, (state) => {
    return state === 'CA' || state === 'VI' || state === 'AK';
});

export const selectShiftBonusParams = createSelector(
    selectIsCaViAk,
    selectShiftHours,
    selectPremiumRate,
    selectMarginPayRate,
    selectBlendedPayRate,
    (showShift, shiftHours, otPayRate, basePayRate, blendedPayRate) => ({
        showShift,
        shiftHours,
        otPayRate,
        basePayRate,
        blendedPayRate
    })
);
export const selectMonthlyReimbursementsParams = createSelector(
    selectCarRentalMonthly,
    selectParkingMonthly,
    selectMonthlyCustomReimbursementTitle,
    selectMonthlyCustomReimbursementAmount,
    (car, parking, customTitle, customAmount) => ({
        car,
        parking,
        customTitle,
        customAmount
    })
);

export const selectOneTimeReimbursementsParams = createSelector(
    selectCarRentalOneTime,
    selectFingerprintingOneTime,
    selectParkingOneTime,
    selectScrubsOneTime,
    selectTaxableModulesOneTime,
    selectOneTimeCustomReimbursementTitle,
    selectOneTimeCustomReimbursementAmount,
    (car, fingerprint, parking, scrub, taxable, customTitle, customAmount) => ({
        car,
        fingerprint,
        parking,
        scrub,
        taxable,
        customTitle,
        customAmount
    })
);

export const selectBonusParams = createSelector(
    selectFacilityPaidBonus,
    selectDiscretionaryBonus,
    selectReferralBonus,
    selectAyaPaidBonus,
    (facility, discretionary, referral, ayapaidBonus) => ({
        facility,
        discretionary,
        referral,
        ayapaidBonus
    })
);

export const selectMarginNotes = createSelector(selectMargin, (margin: MarginState) => margin?.internalCostNotes ?? '');

//#region Load Facility Status
export const selectLoadFacilityStatus = createSelector(
    selectMarginCalculatorState,
    (state: MarginCalculatorState) => state.loadFacilityStatus
);

/** Indicates whether facility loading is in progress */
export const selectLoadFacilityStatusInProgress = createSelector(
    selectLoadFacilityStatus,
    (status: ApiActionStatus) => status === ApiActionStatus.InProgress
);

/** Indicates whether facility loading was successful */
export const selectLoadFacilityStatusSuccess = createSelector(
    selectLoadFacilityStatus,
    (status: ApiActionStatus) => status === ApiActionStatus.Success
);

/** Indicates whether facility loading failed */
export const selectLoadFacilityStatusFailed = createSelector(
    selectLoadFacilityStatus,
    (status: ApiActionStatus) => status === ApiActionStatus.Failed
);
//#endregion

//#region Clinician load status
export const selectLoadClinicianStatus = createSelector(
    selectMarginCalculatorState,
    (state: MarginCalculatorState) => state.loadClinicianStatus
);

/** Indicates whether Clinician loading is in progress */
export const selectLoadClinicianStatusInProgress = createSelector(
    selectLoadClinicianStatus,
    (status: ApiActionStatus) => status === ApiActionStatus.InProgress
);

/** Indicates whether Clinician loading was successful */
export const selectLoadClinicianStatusSuccess = createSelector(
    selectLoadClinicianStatus,
    (status: ApiActionStatus) => status === ApiActionStatus.Success
);

/** Indicates whether Clinician loading failed */
export const selectLoadClinicianStatusFailed = createSelector(
    selectLoadClinicianStatus,
    (status: ApiActionStatus) => status === ApiActionStatus.Failed
);
//#endregion

//#region Minimum Wage
export const selectMinimumWageDetails = createSelector(
    selectMarginCalculatorState,
    (state: MarginCalculatorState) => state.minimumWageDetails
);

export const selectMinimumWageValue = createSelector(
    selectMinimumWageDetails,
    (minWage: MinimumWageDetails) => minWage?.wage ?? 0
);

export const selectIsExtendedMinWageInViolation = createSelector(
    selectMinimumWageValue,
    selectMarginPayRate,
    (minimumWage, basePayRate) => minimumWage > basePayRate
);

export const selectIsOtPayRateInViolation = createSelector(
    selectMinPremiumPayRate,
    selectOtPay,
    (minPayRate, otPayRate) => minPayRate > otPayRate
);

export const selectFacilityMinOTPayRateViolationMessage = createSelector(
    selectMinPremiumPayRate,
    selectOtPay,
    (minPayRate, otPayRate) =>
        minPayRate > otPayRate
            ? `The min OT Pay Rate is $${otPayRate} where $${minPayRate} is the min OT Pay Rate value.`
            : ``
);

//#endregion

export const selectShowOfferTooltip = createSelector(
    selectIsOfferMargin,
    selectChangeOfContractMarginId,
    selectContinueWithoutLinking,
    selectFeatureFlags,
    (
        isOfferMargin: boolean,
        changeOfContractMarginId: number,
        continueWithoutLinking: boolean,
        featureFlags: FeatureFlagState
    ) =>
        featureFlags[PathfindersFlags.OfferMarginLinkingEnabled] === true &&
        !isOfferMargin &&
        !changeOfContractMarginId &&
        !continueWithoutLinking
);

export const selectMarginAsModel = createSelector(
    selectMargin,
    selectProfit,
    selectWeeks,
    selectReimbursementsGrossTotal,
    selectActualMargin,
    selectWeeklyGrossPayTotal,
    selectAccountManagerName,
    selectAccountManagerId,
    selectInsuranceProviderId,
    selectBillingTotalHours,
    selectExtDiscount,
    selectAndOrBillingRule,
    (
        marginState: MarginState,
        marginDollars: number,
        weeks: number,
        reimbursementTotal: number,
        actualMargin: number,
        weeklyGrossPayTotal: number,
        accountManager: string,
        accountManagerId: number,
        benefitsProviderId: number,
        totalHours: number,
        extDiscount: number,
        andOrBillingRule: string
    ) => {
        const margin = {
            ...marginState,
            marginDollars,
            medicalMonth: MarginCalculatorStateHelpers.getTotalMonths(weeks),
            dentalMonth: MarginCalculatorStateHelpers.getTotalMonths(weeks),
            reimbursementTotal: reimbursementTotal ?? 0,
            marginPct: new Big(actualMargin).times(100).round(2, Big.roundHalfUp).toNumber(),
            estimatedWeeklyCheckPretax: weeklyGrossPayTotal ?? 0,
            accountManager,
            accountManagerId,
            benefitsProviderId,
            totalHours,
            extDiscount,
            andOr: MarginCalculatorStateHelpers.convertAndOrRuleToModel(andOrBillingRule)
        } as unknown as MarginState;

        return MarginCalculatorStateHelpers.convertToModel(margin);
    }
);

export const selectFacilityFeesExtensionCost = createSelector(
    selectIsExtension,
    selectBaseBillingRateValue,
    selectExtensionFees,
    selectPayRegularHours,
    selectOverTimeRate,
    selectPayOvertimeHours,
    (isExtension, baseBillingRateValue, extDiscount, regularHours, otrate, othours) => {
        const regularCost = new Big(baseBillingRateValue).times(extDiscount).times(regularHours).div(100);
        const otCost = new Big(otrate).times(extDiscount).times(othours).div(100);
        return isExtension ? new Big(regularCost).add(otCost).toNumber() : 0;
    }
);

export const selectFacilityFeesGrossTotal = createSelector(
    selectFacilityFeesContract,
    selectFacilityFeesEarlyPayment,
    selectFacilityFeesExtensionCost,
    (contractFees, earlyPaymentFees, extensionFees) =>
        new Big(contractFees).add(earlyPaymentFees).add(extensionFees).round(2, Big.roundHalfUp).toNumber()
);

export const selectPremiumBillingRateRequest = createSelector(
    selectFacilityState,
    selectFirstOvertimeFrequency,
    selectAndOrBillingRule,
    selectFirstOvertimeHours,
    selectSecondOvertimeFrequency,
    selectSecondOvertimeHours,
    selectBaseBillingRateValue,
    selectOverTimeRate,
    selectShiftHours,
    selectShifts,
    selectBillingOvertimeEnabled,
    selectBillingRates,
    (
        state,
        otWeekOrDayFirst,
        andOrFirst,
        ruleHoursFirst,
        otWeekOrDaySecond,
        ruleHoursSecond,
        baseBillRate,
        overtimeBillRate,
        shiftLength,
        shiftsPerWeek,
        otBillingRulesEnabled,
        billingRates
    ) => {
        const hasMultipleOtRules =
            PayBillingStateHelpers.hasMultipleRules('overTime', shiftLength, billingRates) ||
            (andOrFirst !== '-' && andOrFirst !== '');
        const request: PremiumBillingRateRequest = {
            state,
            otWeekOrDayFirst,
            andOrFirst,
            ruleHoursFirst,
            otWeekOrDaySecond,
            ruleHoursSecond,
            baseBillRate,
            overtimeBillRate,
            shiftLength,
            shiftsPerWeek
        };

        if (!otBillingRulesEnabled) {
            request.otWeekOrDayFirst = '';
            request.ruleHoursFirst = 0;
        } else if (hasMultipleOtRules) {
            request.otWeekOrDayFirst = otWeekOrDayFirst;
            request.otWeekOrDaySecond = otWeekOrDaySecond;
            request.ruleHoursFirst = ruleHoursFirst;
            request.ruleHoursSecond = ruleHoursSecond;
        } else {
            request.otWeekOrDayFirst = otWeekOrDayFirst;
            request.ruleHoursFirst = ruleHoursFirst;
        }

        if (request.otWeekOrDaySecond === undefined) {
            request.otWeekOrDaySecond = '';
        }

        if (request.ruleHoursSecond === undefined) {
            request.ruleHoursSecond = 0;
        }
        return request;
    }
);

export const selectIsCallbackPayInvalid = createSelector(
    selectFirstBillRateIncludeCallback,
    selectIsCallbackPayRateLessThanMinimum,
    selectCallbackPayRate,
    (isCallbackPayrateIncluded, callbackPayRateLessThanMin, callbackPayRate) => {
        return (
            (isCallbackPayrateIncluded && callbackPayRateLessThanMin) ||
            (!isCallbackPayrateIncluded && callbackPayRate > 0 && callbackPayRateLessThanMin)
        );
    }
);

export const selectIsCallbackPayInvalidForNoCallbackFacility = createSelector(
    selectFirstBillRateIncludeCallback,
    selectCallbackPayRate,
    (isCallbackPayrateIncluded, callbackPayRate) => {
        return !isCallbackPayrateIncluded && callbackPayRate > 0;
    }
);

export const selectIsWageInViolation = createSelector(
    selectIsCallbackPayInvalid,
    selectIsHolidayPayRateLessThanMinium,
    selectIsOtPayRateInViolation,
    selectIsExtendedMinWageInViolation,
    (isCallbackPayInvalid, isHolidayPayRateLessThanMinium, isOTPayRateInViolation, isExtendedMinWageInViolation) =>
        isCallbackPayInvalid || isHolidayPayRateLessThanMinium || isOTPayRateInViolation || isExtendedMinWageInViolation
);

/** Returns
 * isOverRTOLimit: if the amount of consecutive RTO days is over the limit
 * rtoLimit: What the limit is if the consecutive RTO days is over the limit
 */
export const selectRTOLimitResult = createSelector(
    selectVacationDatesAsDates,
    selectNeedInsurance,
    selectIsExtension,
    selectIsRecruiter,
    (vacationDays, needInsurance, isExtension, isRecruiter) => {
        const rtoLimit: RtoLimit = {
            isOverRTOLimit: false,
            rtoLimit: 0
        };

        if (!isExtension && isRecruiter && vacationDays.length > 24) {
            const longestConsecutiveRTODays = MarginCalculatorStateHelpers.findLongestConsecutiveDates(vacationDays);
            const limit = needInsurance ? 24 : 30;
            rtoLimit.isOverRTOLimit = longestConsecutiveRTODays > limit;
            rtoLimit.rtoLimit = rtoLimit.isOverRTOLimit ? limit : 0;
        }
        return rtoLimit;
    }
);

export const selectRTOTooltip = createSelector(
    selectRTOLimitResult,
    selectNeedInsurance,
    (rtoLimitResult, needInsurance) => {
        return rtoLimitResult.isOverRTOLimit
            ? `No more than ${rtoLimitResult.rtoLimit} days consecutive RTO${needInsurance ? ' with insurance.' : '.'}`
            : null;
    }
);

//region Max RTO Validation for extension Contracts
export const selectPreviousContract = createSelector(
    selectInsuranceInfo,
    (insurance: Insurance) => insurance?.previousContract ?? null
);

export const selectPreviousContractEndDateAsDate = createSelector(
    selectPreviousContract,
    (previousContract: ContractBenefit) =>
        previousContract?.contractEndDate ? new Date(previousContract?.contractEndDate) : null
);

export const selectPreviousContractVacationDaysAsDate = createSelector(
    selectPreviousContract,
    (previousContract: ContractBenefit) =>
        previousContract?.vacationDays != null && previousContract.vacationDays !== ''
            ? previousContract.vacationDays.split(';').map((value) => new Date(value))
            : []
);

export const selectRTOMultiContractMessageFlag = createSelector(
    selectFeatureFlags,
    (featureFlags: FeatureFlagState) => featureFlags?.[PathfindersFlags.EnableRTOMultiContractMessage] ?? false
);

export const selectLastVacationDayOfPrevContract = createSelector(
    selectPreviousContractVacationDaysAsDate,
    (previousVacationasDates) =>
        previousVacationasDates?.length > 0 ? previousVacationasDates[previousVacationasDates.length - 1] : null
);
export const maxRtoWithInsurance = 24;
export const maxRtoWithoutInsurance = 30;
export const millisecondsPerDay = 24 * 60 * 60 * 1000;

export const selectAreContractsConsecutive = createSelector(
    selectPreviousContractEndDateAsDate,
    selectStartDateAsDate,
    (prevContractEndDate: Date, contractStartDate: Date) => {
        if (!prevContractEndDate || !contractStartDate) {
            return false;
        }
        return prevContractEndDate.getTime() + millisecondsPerDay === contractStartDate.getTime();
    }
);
export const selectMultipleContractsWithVacationB2B = createSelector(
    selectPreviousContractEndDateAsDate,
    selectLastVacationDayOfPrevContract,
    selectVacationDatesAsDates,
    selectStartDateAsDate,
    selectIsExtension,
    selectAreContractsConsecutive,
    selectRTOMultiContractMessageFlag,
    (
        prevContractEndDate: Date,
        lastVacationDayOfPrevContract,
        vacationDays,
        contractStartDate,
        isExtension,
        areContractsConsecutive,
        isRTOMultiContractMessageFlag
    ) => {
        if (
            isRTOMultiContractMessageFlag &&
            isExtension &&
            prevContractEndDate != null &&
            areContractsConsecutive &&
            lastVacationDayOfPrevContract !== null &&
            lastVacationDayOfPrevContract.getTime() === prevContractEndDate.getTime() &&
            vacationDays[0] != null &&
            contractStartDate.getTime() === vacationDays[0].getTime()
        ) {
            return true;
        }
        return false;
    }
);

export const selectContinousRtoDays = createSelector(
    selectPreviousContract,
    selectPreviousContractEndDateAsDate,
    selectPreviousContractVacationDaysAsDate,
    selectLastVacationDayOfPrevContract,
    selectVacationDatesAsDates,
    selectStartDateAsDate,
    selectIsExtension,
    selectAreContractsConsecutive,
    (
        previousContract: ContractBenefit,
        prevContractEndDate,
        previousVacationAsDates,
        lastVacationDayOfPrevContract,
        vacationDays,
        contractStartDate,
        isExtension,
        areContractsConsecutive
    ) => {
        if (
            (previousContract?.vacationDays == null ||
                (previousContract?.vacationDays !== null &&
                    previousContract?.vacationDays !== '' &&
                    !areContractsConsecutive)) &&
            isExtension &&
            vacationDays?.length > maxRtoWithInsurance
        ) {
            // UC1: single contract with no previous contract or previous contract with non consecutive dates
            return MarginCalculatorStateHelpers.findLongestConsecutiveDates(vacationDays); // or multiple contracts with previous contract with no vaction days
        } else {
            const totalVacationDays = previousVacationAsDates.concat(vacationDays); //UC2: multiple contracts with previous contract with vacation days
            return isExtension &&
                previousContract != null &&
                prevContractEndDate.getTime() < contractStartDate.getTime() &&
                lastVacationDayOfPrevContract !== null &&
                lastVacationDayOfPrevContract.getTime() === prevContractEndDate.getTime() &&
                vacationDays[0] != null &&
                contractStartDate.getTime() === vacationDays[0].getTime() &&
                areContractsConsecutive &&
                totalVacationDays.length > maxRtoWithInsurance
                ? MarginCalculatorStateHelpers.findLongestConsecutiveDates(totalVacationDays)
                : 0;
        }
    }
);

export const selectMaxRtoToolTipForMultiOrSingleExtensions = createSelector(
    selectIsRecruiter,
    selectNeedInsurance,
    selectContinousRtoDays,
    selectMultipleContractsWithVacationB2B,
    (isRecruiter, needInsurance, continousRtoDays, isMultipleContractsWithVacationB2B) => {
        if (!isRecruiter) {
            return '';
        }

        const maxRtoDays = needInsurance ? maxRtoWithInsurance : maxRtoWithoutInsurance;
        const insuranceType = needInsurance ? 'with insurance' : 'without insurance';
        const contractSuffix = isMultipleContractsWithVacationB2B ? ' across multiple contracts' : '';

        if (continousRtoDays > maxRtoDays) {
            return `No more than ${maxRtoDays} days consecutive RTO ${insuranceType}${contractSuffix}.`;
        }

        return '';
    }
);

//#endegion Max RTO Validation for extension Contracts

export const selectPinkFacility = createSelector(selectIsFacilityInvalid, (isFacilityInvalid) => isFacilityInvalid);

export const selectIsMarginSaveEnabled = createSelector(
    selectContractId,
    selectIsCallbackPayInvalid,
    selectCallbackPayRate,
    selectIsHolidayPayRateLessThanMinium,
    selectHolidayPayRate,
    selectIsOtPayRateInViolation,
    selectOtPay,
    selectIsExtendedMinWageInViolation,
    selectIsNurseContract,
    selectMaxRtoToolTipForMultiOrSingleExtensions,
    selectRTOLimitResult,
    (
        contractId,
        isCallbackPayInvalid,
        callbackPayRate,
        isHolidayLessThanMinium,
        holidayPayRate,
        isOTPayRateInViolation,
        otPayRate,
        isExtendedMinWageInViolation,
        isNurseContract,
        maxRtoTooltipForExtensionContracts,
        rtoLimitResult
    ) =>
        !contractId
            ? !(
                  (isCallbackPayInvalid && callbackPayRate) ||
                  (isHolidayLessThanMinium && holidayPayRate) ||
                  (isOTPayRateInViolation && otPayRate) ||
                  isExtendedMinWageInViolation ||
                  maxRtoTooltipForExtensionContracts !== '' ||
                  rtoLimitResult.isOverRTOLimit
              ) || isNurseContract
            : !contractId
);

export const selectSaveTooltip = createSelector(
    selectContractId,
    selectIsCallbackPayInvalid,
    selectCallbackPayRate,
    selectIsHolidayPayRateLessThanMinium,
    selectHolidayPayRate,
    selectIsOtPayRateInViolation,
    selectOtPay,
    selectIsExtendedMinWageInViolation,
    selectIsNurseContract,
    selectMaxRtoToolTipForMultiOrSingleExtensions,
    selectRTOTooltip,
    (
        contractId,
        isCallbackPayInvalid,
        callbackPayRate,
        isHolidayPayRateLessThanMinium,
        holidayPayRate,
        isOTPayRateInViolation,
        otPayRate,
        isExtendedMinWageInViolation,
        isNurseContract,
        maxRtoToolTipForSingleOrMultipleContracts,
        rtoTooltip
    ) => {
        if (!isNurseContract && !contractId) {
            const violations: string[] = [];
            const rtoToolTipMessage = `${maxRtoToolTipForSingleOrMultipleContracts}${rtoTooltip == null ? '' : rtoTooltip}`;

            if (isExtendedMinWageInViolation) {
                violations.push('Base Pay Rate');
            }
            if (otPayRate && isOTPayRateInViolation) {
                violations.push('OT Pay Rate');
            }
            if (callbackPayRate && isCallbackPayInvalid) {
                violations.push('Callback Pay Rate');
            }
            if (holidayPayRate && isHolidayPayRateLessThanMinium) {
                violations.push('Holiday Pay Rate');
            }
            if (violations.length > 0) {
                const tooltip = `Minimum wage is in violation. Please increase ${violations.join(', ').replace(/, ([^,]*)$/, ' and $1')} to save the margin.`;
                return rtoToolTipMessage ? `${tooltip} ${rtoToolTipMessage}` : tooltip;
            } else {
                return rtoToolTipMessage;
            }
        }
        return null;
    }
);

export const selectReviewAndSendButtonDisabled = createSelector(
    selectMarginExists,
    selectIsPipelined,
    selectReviewAndSendEnabled,
    selectIsOfferMargin,
    selectChangeOfContractMarginId,
    selectContinueWithoutLinking,
    selectIsMarginSaveEnabled,
    selectFeatureFlags,
    selectIsLinkedContractRequestInDraftingStatus,
    (
        marginExists: boolean,
        isPipelined: boolean,
        reviewAndSendEnabled: boolean,
        isOfferMargin: boolean,
        changeOfContractMarginId: number,
        continueWithoutLinking: boolean,
        isMarginSaveEnabled: boolean,
        featureFlags: FeatureFlagState,
        isLinkedContractRequestInDraftingStatus: boolean
    ) =>
        !marginExists ||
        isPipelined ||
        !reviewAndSendEnabled ||
        (featureFlags[PathfindersFlags.OfferMarginLinkingEnabled] === true &&
            !isOfferMargin &&
            !changeOfContractMarginId &&
            !continueWithoutLinking) ||
        !isMarginSaveEnabled ||
        isLinkedContractRequestInDraftingStatus
);

export const selectReviewAndSendTooltip = createSelector(
    selectShowOfferTooltip,
    selectIsWageInViolation,
    selectSaveTooltip,
    selectMarginExists,
    (showOffer: boolean, showMinimumWage: boolean, saveTooltip: string, marginExists: boolean) => {
        if (marginExists && showOffer) {
            return 'Margin is not currently linked to an Offer. Please tap the link in the red banner for options to proceed.';
        } else if (marginExists && showMinimumWage) {
            return saveTooltip;
        }
        return null;
    }
);

export const selectMoneyMovementValidations = createSelector(
    selectMarginCalculatorState,
    (state: MarginCalculatorState) => state?.moneyMovementValidations ?? []
);

export const selectEnableAyaPaidBonusCoCApprovalFeatureFlag = createSelector(
    selectFeatureFlags,
    (featureFlags: FeatureFlagState) => featureFlags?.[PathfindersFlags.EnableAyaPaidBonusCoCApprovalWorkFlow] ?? false
);

export const selectContractRequestData = createSelector(
    selectMarginId,
    selectMarginPercent,
    selectRecruiterId,
    selectFacilityId,
    selectClinicianId,
    selectEnableAyaPaidBonusCoCApprovalFeatureFlag,
    (marginId, marginPct, recruiterId, facilityId, candidateId, ayaPaidBonusCoCApprovalEnabled) => ({
        marginId,
        marginPct,
        recruiterId,
        facilityId,
        candidateId,
        ayaPaidBonusCoCApprovalEnabled
    })
);

//#region RTO Alert On Start Date and End Date

export const selectEnableRTOAlertMessage = createSelector(
    selectStartDateAsDate,
    selectEndDateAsDate,
    selectVacationDatesAsDates,
    (startDate: Date, endDate: Date, vacationDates: Date[]) => {
        return vacationDates !== null && vacationDates !== undefined && vacationDates.length > 0
            ? startDate?.getTime() === vacationDates[0]?.getTime() ||
                  endDate?.getTime() === vacationDates[vacationDates.length - 1]?.getTime()
            : false;
    }
);

export const selectDateClass = createSelector(
    selectVacationDatesAsDates,
    selectStartDateAsDate,
    selectEndDateAsDate,
    selectMarginFormDisabled,
    (vacationDates, contractStartDate, contractEndDate, formDisabled) => {
        return MarginCalculatorStateHelpers.buildVacationCalendarCellCssClasses(
            vacationDates,
            contractStartDate,
            contractEndDate,
            formDisabled
        );
    }
);

//#endregion RTO alert on start date and end date
//#region Aya paid bonus validation
export const selectIsAyaPaidBonusValid = createSelector(
    selectOfferAyaPaidBonus,
    selectAyaPaidBonus,
    (offerAyaPaidBonus, marginAyaPaidBonus) => offerAyaPaidBonus === marginAyaPaidBonus
);

export const selectAyaPaidBonusValidationMessage = createSelector(
    selectIsAyaPaidBonusValid,
    selectIsOfferMargin,
    (isAyaPaidBonusValid, isOfferMargin) => {
        if (isAyaPaidBonusValid) {
            return '';
        }
        if (!isOfferMargin) {
            return 'No linked Offer to validate Aya Paid Bonus amount against.';
        }

        return 'Aya Paid Bonus amount does not match the amount listed on the Offer.';
    }
);

//#end region Aya paid bonus validation

export const selectPinkMedicalEffective = createSelector(
    selectMarginMedicalEffectiveDate,
    selectIsExtendedBenefits,
    selectLastEnrollmentDate,
    selectMarginMedicalEffectiveDateAsDate,
    selectLastEnrollmentDateAsDate,
    selectStartDateAsDate,
    (
        medicalEffectiveDate,
        isExtendedBenefits,
        lastEnrollmentDate,
        medicalEffectiveDateAsDate,
        lastEnrollmentDateAsDate,
        startDateAsDate
    ) => {
        if (!medicalEffectiveDate) {
            return false;
        }
        // Not extended benefits: must be different from start date
        if (!isExtendedBenefits) {
            return !MarginCalculatorStateHelpers.isSameDate(medicalEffectiveDateAsDate, startDateAsDate);
        }
        // Extended benefits, no previous enrollment: must be different from start date
        if (isExtendedBenefits && !lastEnrollmentDate) {
            return !MarginCalculatorStateHelpers.isSameDate(medicalEffectiveDateAsDate, startDateAsDate);
        }
        // Extended benefits, previous enrollment exists: must not be same as lastEnrollmentDate + 1 day
        if (isExtendedBenefits && lastEnrollmentDate && medicalEffectiveDateAsDate && lastEnrollmentDateAsDate) {
            return !MarginCalculatorStateHelpers.isSameDate(
                medicalEffectiveDateAsDate,
                addDays(lastEnrollmentDateAsDate, 1)
            );
        }
        // Default fallback
        return false;
    }
);

export const selectPinkDentalEffective = createSelector(
    selectMarginDentalEffectiveDate,
    selectIsExtendedBenefits,
    selectLastEnrollmentDate,
    selectMarginDentalEffectiveDateAsDate,
    selectLastEnrollmentDateAsDate,
    selectStartDateAsDate,
    (
        dentalEffectiveDate,
        isExtendedBenefits,
        lastEnrollmentDate,
        dentalEffectiveDateAsDate,
        lastEnrollmentDateAsDate,
        startDateAsDate
    ) => {
        if (!dentalEffectiveDate) {
            return false;
        }
        // Not extended benefits: must be different from start date
        if (!isExtendedBenefits) {
            return !MarginCalculatorStateHelpers.isSameDate(dentalEffectiveDateAsDate, startDateAsDate);
        }
        // Extended benefits, no previous enrollment: must be different from start date
        if (isExtendedBenefits && !lastEnrollmentDate) {
            return !MarginCalculatorStateHelpers.isSameDate(dentalEffectiveDateAsDate, startDateAsDate);
        }
        // Extended benefits, previous enrollment exists: must not be same as lastEnrollmentDate + 1 day
        if (isExtendedBenefits && lastEnrollmentDate && dentalEffectiveDateAsDate && lastEnrollmentDateAsDate) {
            return !MarginCalculatorStateHelpers.isSameDate(
                dentalEffectiveDateAsDate,
                addDays(lastEnrollmentDateAsDate, 1)
            );
        }
        // Default fallback
        return false;
    }
);

export const selectIsVisibleMedicalDentalEffectiveDates = createSelector(
    selectIsPipelined,
    selectSavedAfterMedicalDentalEffectiveInsurance,
    (isPipelined, savedAfterMedicalDentalEffectiveInsurance) => {
        return (isPipelined && savedAfterMedicalDentalEffectiveInsurance) || !isPipelined;
    }
);

export const selectRedBannerContractRequestSentFixFlag = createSelector(
    selectFeatureFlags,
    (featureFlags: FeatureFlagState) =>
        (featureFlags && featureFlags[PathfindersFlags.RedBannerContractRequestSentFix]) ?? false
);

export const selectShowMarginNotLinkedToOfferBanner = createSelector(
    selectRedBannerContractRequestSentFixFlag,
    selectIsLinkedContractRequestInDraftingStatus,
    selectIsRecruiter,
    selectMarginExists,
    selectIsOfferMargin,
    selectIsPipelined,
    selectChangeOfContractMarginId,
    (
        redBannerContractRequestSentFixFlag,
        isLinkedContractRequestInDraftingStatus,
        isRecruiter,
        marginExists,
        isOfferMargin,
        isPipelined,
        changeOfContractMarginId
    ) => {
        return (
            ((redBannerContractRequestSentFixFlag && !isLinkedContractRequestInDraftingStatus) ||
                !redBannerContractRequestSentFixFlag) &&
            isRecruiter &&
            marginExists &&
            !isOfferMargin &&
            !isPipelined &&
            !changeOfContractMarginId
        );
    }
);

export const selectFixClinicianBlurIssueFlag = createSelector(
    selectFeatureFlags,
    (featureFlags: FeatureFlagState) => (featureFlags && featureFlags[PathfindersFlags.FixClinicianBlurIssue]) ?? false
);

export const selectMarginTypeId = createSelector(
    selectMargin,
    selectFeatureFlags,
    (margin: MarginState, featureFlags: FeatureFlagState) =>
        featureFlags?.[PathfindersFlags.WFDSimplifiedSampleMargin] ? margin?.marginTypeId : MarginType.Travel
);

export const selectMarginLinkedToJob = createSelector(
    selectMarginCalculatorState,
    (state: MarginCalculatorState) => state?.linkedToJob ?? false
);

export const selectIsMcPristineFacilityEnabledFlag = createSelector(
    selectFeatureFlags,
    (featureFlags: FeatureFlagState) => featureFlags?.[PathfindersFlags.IsMcPristineFacilityEnabled] ?? false
);

export const selectFacilityGlobalKey = createSelector(
    selectFacility,
    (facility: Facility) => facility?.globalFacilityKey
);
